import { useState, useMemo, useRef, useEffect } from "react";
import type { ReactNode, ChangeEvent, MouseEvent as ReactMouseEvent } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { openResumeInTab } from "../../lib/openResume.js";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiFetch, apiUpload, ApiError, fileToBase64 } from "../../lib/api.js";
import { CvUploadStatus, cvProblem, type CvUploadItem } from "./CvUploadStatus.js";
import { CandidateTimeline } from "../../components/CandidateTimeline.js";
import { serverMessage } from "./planUsage.js";
import { relativeActivityDate } from "../../lib/format.js";
import { RequirementCombobox } from "../../components/RequirementCombobox.js";
import { ConnectivityModal } from "../../components/ConnectivityModal.js";
import { rankBucketLabel, type RankBucket, type RankingBasis } from "@talenthop/shared";
import { displayTitle } from "./displayTitle.js";
import { getRequisitionStatus } from "./requisitionFormat.js";
import {
  Users,
  Filter,
  Plus,
  Link2,
  Search,
  Check,
  X,
  FileText,
  ExternalLink,
  UploadCloud,
  Mail,
  Copy,
  Send,
  ChevronDown,
  Info,
  Alert,
  Loader2,
} from "../../components/icons.js";
import { funnelStageFromParam, reachedStage } from "./funnelStages.js";
import { FunnelStageChip } from "./FunnelStageChip.js";
import { Modal } from "../admin/adminUi.js";

interface RequisitionRow {
  id: string;
  title: string;
  state: string;
  candidateCount?: number;
}

interface RequisitionsResponse {
  requisitions: RequisitionRow[];
}

interface FunnelCandidate {
  candidacyId: string;
  name: string | null;
  email?: string | null;
  state: string;
  resumeScore: number | null;
  score?: number | null;
  band?: string | null;
  createdAt?: string | null;
}

interface FunnelResponse {
  requisition: { id: string; title: string };
  candidates: FunnelCandidate[];
}

/** GET /org/candidates — one row per candidacy across the org's requisitions. */
interface OrgCandidate {
  candidacyId: string;
  name: string | null;
  email: string | null;
  requisitionId: string;
  requisitionTitle: string;
  state: string;
  pipelineStatus: string | null;
  resumeScore: number | null;
  /** 'email' for an inbound application (0083); null on a row predating source tracking. */
  source: string | null;
  sourceDetail: string | null;
  hasResume: boolean;
  resumeFilename: string | null;
  scanId: string | null;
  /** False for a CV on file that has never been matched. */
  scanScored: boolean;
  resumeSource: string | null;
  interviewStarted: boolean;
  interviewScored: boolean;
  /** The shared ranking of the current evaluation (API: rankBy) — the only source of interview ordering. */
  rank?: number;
  /** The question-level INTERVIEW score (0105), null = not scored yet; `competencyScore` is the separate competency measure. */
  interviewScore?: number | null;
  competencyScore?: number | null;
  rankingBasis?: RankingBasis;
  coverage?: number | null;
  rankBucket?: RankBucket;
  createdAt: string;
  /** Latest CV/details request (bulk import or Request CV), persisted server-side. */
  cvRequest?: CvRequest | null;
}

interface CvRequest {
  status: "sent" | "opened" | "completed" | "expired" | "cancelled";
  requestedAt: string;
  expiresAt: string | null;
  openedAt: string | null;
  /** queued · emailed (handed to the provider) · not_sent (email off here) · failed · null (unknown) */
  email: "queued" | "emailed" | "not_sent" | "failed" | null;
}

/** "Requested 3 Oct · emailed" — what a recruiter needs before deciding to ask again. */
function cvRequestSummary(r: CvRequest | null | undefined): string | null {
  if (!r) return null;
  const when = new Date(r.requestedAt).toLocaleDateString(undefined, { day: "numeric", month: "short" });
  if (r.status === "expired") return `Requested ${when} · link expired`;
  if (r.status === "opened") return `Requested ${when} · candidate opened the link`;
  const email =
    r.email === "emailed"
      ? "emailed"
      : r.email === "queued"
        ? "email queued"
        : r.email === "failed"
          ? "email failed — share the link"
          : r.email === "not_sent"
            ? "email is off — share the link"
            : null;
  return email ? `Requested ${when} · ${email}` : `Requested ${when}`;
}

interface OrgCandidatesResponse {
  candidates: OrgCandidate[];
}

export interface CandidateResumeItem {
  id: string;
  candidacyId?: string;
  name: string;
  email: string;
  reqId: string;
  reqTitle: string;
  channel: "In-System Email" | "Connected email (Outlook)" | "Connected email (Gmail)" | "ATS Webhook" | "Direct Upload" | "Direct HR Add";
  systemStatus: "ai_matched" | "extracted" | "match_skipped" | "pending_resume";
  pipelineStage: "matches" | "shortlisted" | "screening" | "interview" | "rejected";
  matchScore: number | null;
  isInterviewed: boolean;
  interviewInviteSent?: boolean;
  interviewScore?: number;
  rankingBasis?: RankingBasis;
  /** Position in the API's ranking (1 = best); absent until evaluated. */
  interviewRank?: number;
  interviewBucket?: RankBucket;
  hasResume: boolean;
  missingResumeEmailSent?: boolean;
  cvRequest?: CvRequest | null;
  roleTitle: string;
  company: string;
  experienceYears: number;
  location: string;
  skills: string[];
  resumeFileName?: string;
  /** The resume_scans row behind this candidate, when a CV is on file — what Run AI Match scores. */
  scanId?: string | null;
  scanScored?: boolean;
  initials: string;
  avatarColor: "blue" | "yellow" | "teal" | "purple";
  status: "pending" | "approved" | "rejected";
  /** When this candidate was added — the activity date shown on the row. */
  createdAt?: string | null;
}


const SKILL_FILTER_CHIPS = [
  ".NET Core / C#",
  "ASP.NET Core WebAPI",
  "SQL Server",
  "Microservices",
  "Azure Cloud",
  "Go / Golang",
  "Distributed Systems",
  "PostgreSQL",
  "Kafka",
  "Docker / K8s",
];

export type PipelineTab = "all" | "matches" | "shortlisted" | "screening" | "interview" | "cv_requested";

/** A MATCH is a resume the AI scored 70%+ against the requirement — the same bar the Requirements
 *  index uses. (This used to count every scored or extracted resume, so Matches always equalled All.) */
const isMatch = (c: CandidateResumeItem): boolean =>
  c.systemStatus === "ai_matched" && c.matchScore != null && c.matchScore >= 70;

export const STAGE_CONFIG = {
  matches: {
    label: "Matches",
    dotColor: "var(--lime-strong)",
    bgColor: "var(--teal-wash)",
    textColor: "var(--teal)",
    borderColor: "color-mix(in srgb, var(--teal) 35%, var(--line))",
  },
  shortlisted: {
    label: "Shortlisted",
    dotColor: "var(--violet)",
    bgColor: "var(--violet-wash)",
    textColor: "var(--violet)",
    borderColor: "color-mix(in srgb, var(--violet) 35%, var(--line))",
  },
  screening: {
    label: "Screening",
    dotColor: "var(--orange)",
    bgColor: "var(--warn-wash)",
    textColor: "var(--warn)",
    borderColor: "color-mix(in srgb, var(--warn) 35%, var(--line))",
  },
  interview: {
    label: "Interview",
    dotColor: "var(--violet)",
    bgColor: "var(--violet-wash)",
    textColor: "var(--violet)",
    borderColor: "color-mix(in srgb, var(--violet) 35%, var(--line))",
  },
} as const;

/**
 * Visual style for a source/channel badge, by the exact string CandidateResumeItem.channel
 * already carries — no new source values, just a consistent bordered pill for whichever ones
 * exist today (and a sane default for any added later, e.g. a future ATS connector's own label).
 */
const SOURCE_STYLE: Record<string, { bg: string; border: string; color: string; dot: string }> = {
  "In-System Email": { bg: "var(--surface-2)", border: "var(--line)", color: "var(--ink-soft)", dot: "var(--lime-strong)" },
  "Connected email (Outlook)": { bg: "var(--surface-2)", border: "var(--line)", color: "var(--ink-soft)", dot: "var(--violet)" },
  "Connected email (Gmail)": { bg: "var(--surface-2)", border: "var(--line)", color: "var(--ink-soft)", dot: "var(--violet)" },
  "ATS Webhook": { bg: "var(--surface-2)", border: "var(--line)", color: "var(--ink-soft)", dot: "var(--violet)" },
  "Direct Upload": { bg: "var(--surface-2)", border: "var(--line)", color: "var(--ink-soft)", dot: "var(--ink-mute)" },
  "Direct HR Add": { bg: "var(--surface-2)", border: "var(--line)", color: "var(--ink-soft)", dot: "var(--ink-mute)" },
};
const DEFAULT_SOURCE_STYLE = { bg: "var(--surface-2)", border: "var(--line)", color: "var(--ink-soft)", dot: "var(--ink-mute)" };

/** Consistent bordered source/channel pill — dot + label, same shape for every source. */
function SourceBadge({ channel }: { channel: string }): ReactNode {
  const s = SOURCE_STYLE[channel] ?? DEFAULT_SOURCE_STYLE;
  return (
    <span className="pill-source" style={{ background: s.bg, borderColor: s.border, color: s.color }}>
      <i style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot, display: "inline-block", flexShrink: 0 }} />
      {channel}
    </span>
  );
}

/**
 * Small circular score ring for the card's right column — visually distinct per metric (colour +
 * caption), so an AI/resume match score and an interview score are never mistaken for each other.
 * Never renders a fabricated number: the caller only mounts this when a real score exists.
 */
function MatchRing({ score, label, color, size = 64 }: { score: number; label: string; color: string; size?: number }): ReactNode {
  const r = 27;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, Math.round(score)));
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
      <svg viewBox="0 0 64 64" width={size} height={size} role="img" aria-label={`${label}: ${clamped}%`}>
        <circle cx="32" cy="32" r={r} fill="none" stroke="var(--line)" strokeWidth="6" />
        <circle
          cx="32"
          cy="32"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={`${(clamped / 100) * c} ${c}`}
          transform="rotate(-90 32 32)"
        />
        <text x="32" y="37" textAnchor="middle" fontSize="15" fontWeight="800" fill="var(--ink)">
          {clamped}%
        </text>
      </svg>
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: "var(--ink-mute)",
          textAlign: "center",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
    </div>
  );
}

/** Score band (audit X3): lime is a strong match, orange a partial one, red a weak one. */
function scoreBand(score: number): "hi" | "mid" | "low" {
  return score >= 70 ? "hi" : score >= 50 ? "mid" : "low";
}

function ringColor(score: number): string {
  const band = scoreBand(score);
  return band === "hi" ? "var(--lime-strong)" : band === "mid" ? "var(--orange)" : "var(--crit)";
}

/**
 * Compact score ring for the table view (docs/ux-audit/theme1/interviews.html). Only mounted for
 * a real score — an unscored row shows a dash, never a zero.
 */
function ScoreRing({ score, label }: { score: number; label: string }): ReactNode {
  const v = Math.max(0, Math.min(100, Math.round(score)));
  return (
    <div
      className={`t1c-ring ${scoreBand(v)}`}
      style={{ ["--v" as string]: v } as React.CSSProperties}
      role="img"
      aria-label={`${label}: ${v}%`}
      title={`${label}: ${v}%`}
    >
      <span>{v}%</span>
    </div>
  );
}

function ArrowRightIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

/** Three-dot glyph for the row overflow menu. */
function MoreIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width={16} height={16} fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="12" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="19" cy="12" r="1.8" />
    </svg>
  );
}

type Density = "table" | "cards";

/** Resume-match bands; several can be on at once and a candidate in ANY of them shows. Empty = any score. */
type ScoreBandKey = "hi" | "mid" | "low" | "unscored";
type ScoreFilter = ScoreBandKey[];
type SourceFilter = "all" | "email" | "direct";

/** The resume-match band and source filters (client-side, same list as every other filter). */
/**
 * A click anywhere on a candidate row/card opens their review page — except on the row's own
 * controls (the Resume link, checkbox, Send interview, "…", stage menu…), which keep their action,
 * and except when the click ends a text selection. Ctrl/⌘-click opens it in a new tab.
 */
function openCandidateFromRow(e: ReactMouseEvent, href: string, go: (to: string) => void): void {
  const target = e.target as HTMLElement;
  if (target.closest("a, button, input, select, textarea, label, [role='menu'], [role='menuitem'], [role='dialog'], [data-row-ignore]")) return;
  if (window.getSelection()?.toString()) return;
  if (e.ctrlKey || e.metaKey) {
    window.open(href, "_blank", "noopener");
    return;
  }
  go(href);
}

function passesScoreAndSource(c: CandidateResumeItem, score: ScoreFilter, source: SourceFilter): boolean {
  if (source === "email" && c.channel !== "In-System Email") return false;
  if (source === "direct" && c.channel === "In-System Email") return false;
  if (score.length === 0) return true;
  const scored = c.systemStatus === "ai_matched" && c.matchScore != null;
  return score.includes(scored ? scoreBand(c.matchScore!) : "unscored");
}

/** One status per row (pill + a short reason), derived only from facts the row already holds. */
function candidateStatus(c: CandidateResumeItem): { label: string; tone: "v" | "t" | "n" | "pv"; reason: string } {
  const stage = STAGE_CONFIG[c.pipelineStage as keyof typeof STAGE_CONFIG]?.label ?? "Rejected";
  if (c.status === "rejected") return { label: "Rejected", tone: "n", reason: "Your decision · undo from the menu" };
  if (c.status === "approved") return { label: "Approved", tone: "v", reason: `Stage: ${stage}` };
  if (c.isInterviewed)
    return {
      label: "Interviewed",
      tone: "t",
      reason: c.interviewScore != null ? `Interview ${c.interviewScore}${c.interviewBucket ? ` · ${rankBucketLabel(c.interviewBucket, c.rankingBasis ?? "interview")}` : ""}` : `Stage: ${stage}`,
    };
  if (c.systemStatus === "pending_resume")
    return {
      label: "Waiting for CV",
      tone: "n",
      reason: cvRequestSummary(c.cvRequest) ?? (c.missingResumeEmailSent ? "CV requested by email" : "No CV on file yet"),
    };
  if (c.systemStatus === "ai_matched") return { label: "AI matched", tone: "t", reason: `Stage: ${stage}` };
  if (c.systemStatus === "extracted") return { label: "Extracted", tone: "n", reason: "Resume read · not scored" };
  return { label: "Not matched", tone: "n", reason: "CV on file · run AI match to score" };
}
const CAND_DENSITY_KEY = "th:cand-density";

type CandSort = "newest" | "oldest" | "match" | "interview" | "experience" | "az" | "za";

const CAND_SORT_KEY = "th:cand-sort";

const CAND_SORTS: { key: CandSort; label: string }[] = [
  { key: "newest", label: "Newest added" },
  { key: "oldest", label: "Oldest added" },
  { key: "match", label: "Best AI match" },
  { key: "interview", label: "Best interview score" },
  { key: "experience", label: "Most experience" },
  { key: "az", label: "Name A–Z" },
  { key: "za", label: "Name Z–A" },
];

/** Unscored candidates always sort after scored ones, whichever score is chosen. */
function candidateSorter(k: CandSort): (a: CandidateResumeItem, b: CandidateResumeItem) => number {
  const at = (c: CandidateResumeItem): number => (c.createdAt ? Date.parse(c.createdAt) : 0);
  const score = (v: number | null | undefined): number => (v == null ? -1 : v);
  switch (k) {
    case "oldest":
      return (a, b) => at(a) - at(b);
    case "match":
      return (a, b) => score(b.matchScore) - score(a.matchScore) || at(b) - at(a);
    case "interview":
      // The API's ranking (coverage- and criteria-aware), not a sort of the bare number.
      return (a, b) => (a.interviewRank ?? Number.MAX_SAFE_INTEGER) - (b.interviewRank ?? Number.MAX_SAFE_INTEGER) || at(b) - at(a);
    case "experience":
      return (a, b) => (b.experienceYears ?? 0) - (a.experienceYears ?? 0) || at(b) - at(a);
    case "az":
      return (a, b) => a.name.localeCompare(b.name);
    case "za":
      return (a, b) => b.name.localeCompare(a.name);
    default:
      return (a, b) => at(b) - at(a);
  }
}

export function ResumesHub(): ReactNode {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const activeReqId = params.get("reqId") || "all";
  // #239: opened for ONE requirement (the requirement page's "View candidates") — the page is that
  // requirement's candidate list: its name is the heading, and there is no requirement to pick.
  const reqScoped = activeReqId !== "all";
  // Opened from a requirement's funnel card: keep only the people that card counted (funnelStages.ts).
  const funnelStage = funnelStageFromParam(params.get("stage"));

  // Filter UI states
  // Filters start open on desktop; on a phone they'd push the list a screen down, so they start
  // closed there (the Filter button opens them).
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      return !window.matchMedia("(max-width: 900px)").matches;
    } catch {
      return true;
    }
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [experienceLevel, setExperienceLevel] = useState<number>(0);
  const [systemStatusFilter, setSystemStatusFilter] = useState<"all" | "ai_matched" | "match_skipped" | "extracted" | "pending_resume">("all");
  // Resume-match band and source — narrow the same client-side list the other filters do.
  const [scoreFilter, setScoreFilter] = useState<ScoreFilter>([]);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");

  // Pipeline Stage Tabs: All | Matches | Shortlisted | Screening | Interview
  const [pipelineTab, setPipelineTab] = useState<PipelineTab>("all");

  // Clickable Sub-Filter for Scoped Metrics Strip
  const [metricSubFilter, setMetricSubFilter] = useState<
    "all" | "with_resume" | "matched" | "interviewed" | "pending_resume"
  >("all");

  // Pagination (30 resumes per page)
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 30;

  // Status Dropdown open state
  const [openStageDropdown, setOpenStageDropdown] = useState<string | null>(null);

  useEffect(() => {
    function handleClickOutside() {
      setOpenStageDropdown(null);
    }
    if (openStageDropdown) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [openStageDropdown]);

  // Row overflow ("…") menu — holds the secondary row actions so each row shows one primary
  // action (audit 16, X10).
  const [rowMenu, setRowMenu] = useState<string | null>(null);
  useEffect(() => {
    if (!rowMenu) return;
    const close = (): void => setRowMenu(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [rowMenu]);

  // Reject is a human decision, so it asks first (GROUND_RULES: rejection is always human-gated).
  const [confirmReject, setConfirmReject] = useState<CandidateResumeItem | null>(null);
  /** "Already requested — request again?" before minting a second link. */
  const [confirmResend, setConfirmResend] = useState<CandidateResumeItem | null>(null);
  /** The link just minted, shown ONCE with a Copy button (only its hash is stored server-side). */
  const [lastCvLink, setLastCvLink] = useState<{ id: string; name: string; link: string } | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const copyCvLink = (link: string) => {
    void navigator.clipboard?.writeText(link).then(() => {
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 1800);
    });
  };

  // List density: a compact table by default (audit top-15 #6); the card view stays one click away.
  const [density, setDensity] = useState<Density>(() => {
    try {
      return localStorage.getItem(CAND_DENSITY_KEY) === "cards" ? "cards" : "table";
    } catch {
      return "table";
    }
  });
  const chooseDensity = (d: Density): void => {
    setDensity(d);
    try {
      localStorage.setItem(CAND_DENSITY_KEY, d);
    } catch {
      /* not remembered */
    }
  };

  // Reset pagination to page 1 whenever filters or tabs change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeReqId, pipelineTab, searchQuery, selectedSkills, experienceLevel, systemStatusFilter, metricSubFilter, scoreFilter, sourceFilter]);

  // Reset metric sub-filter when active requisition or stage tab changes
  useEffect(() => {
    setMetricSubFilter("all");
  }, [activeReqId, pipelineTab]);

  // Selection states
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Modal states
  const [previewCandidate, setPreviewCandidate] = useState<CandidateResumeItem | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [uploadTargetReqId, setUploadTargetReqId] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 3-Mode Add Candidate Modal States
  const [addCandidateMode, setAddCandidateMode] = useState<"bulk_match" | "direct_fasttrack" | "request_email">("bulk_match");
  const [directName, setDirectName] = useState("");
  const [directEmail, setDirectEmail] = useState("");
  const [directRole, setDirectRole] = useState("");

  // Deep link from a requisition's "Upload CVs & score" (#197): ?reqId=<id>&add=upload opens the
  // uploader already pointed at that requisition. The `add` flag is consumed so a reload or
  // back-navigation doesn't pop the dialog again.
  useEffect(() => {
    if (params.get("add") !== "upload") return;
    if (activeReqId !== "all") setUploadTargetReqId(activeReqId);
    setAddCandidateMode("bulk_match");
    setUploadModalOpen(true);
    const next = new URLSearchParams(params);
    next.delete("add");
    setParams(next, { replace: true });
  }, [params, activeReqId, setParams]);
  const [directStage, setDirectStage] = useState<"interview" | "screening" | "shortlisted">("interview");
  const [directExperience, setDirectExperience] = useState<number>(4);
  const [requestName, setRequestName] = useState("");
  const [requestEmail, setRequestEmail] = useState("");
  const [requestCustomNote, setRequestCustomNote] = useState("");

  // Batch Interview Dispatch states
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [batchStep, setBatchStep] = useState<"confirm" | "success">("confirm");
  const [isDispatching, setIsDispatching] = useState(false);
  const [generatedBatchLinks, setGeneratedBatchLinks] = useState<
    Array<{ id: string; name: string; email: string; link: string }>
  >([]);
  const [copiedBatchAll, setCopiedBatchAll] = useState(false);
  const [dispatchError, setDispatchError] = useState<string | null>(null);
  const [dispatchSkipped, setDispatchSkipped] = useState(0);
  useEffect(() => {
    if (batchModalOpen) {
      setDispatchError(null);
      setDispatchSkipped(0);
    }
  }, [batchModalOpen]);
  const [copiedSingleId, setCopiedSingleId] = useState<string | null>(null);

  // Candidate status tracker (approved/rejected)
  const [candidateStates, setCandidateStates] = useState<Record<string, "pending" | "approved" | "rejected">>({});

  // Dynamic pipeline stage overrides
  const [candidateStageOverrides, setCandidateStageOverrides] = useState<Record<string, "matches" | "shortlisted" | "screening" | "interview" | "rejected">>({});

  // Dynamic AI Match overrides (Run AI Match or Skip Matching)
  const [candidateMatchOverrides, setCandidateMatchOverrides] = useState<
    Record<string, { matchScore: number | null; systemStatus: "ai_matched" | "extracted" | "match_skipped" | "pending_resume" }>
  >({});

  // Missing resume email requests tracker
  const [missingResumeInvites, setMissingResumeInvites] = useState<Record<string, boolean>>({});

  // Result of the last add/upload — shown in the page, not an alert(). An alert blocks the tab
  // and cannot say "3 screened, 1 already on file" without sounding like an error.
  const [actionNote, setActionNote] = useState<string | null>(null);

  // Dynamic interview state override (when user dispatches links in session)
  const [interviewOverride, setInterviewOverride] = useState<Record<string, boolean>>({});

  // Fetch live requisitions list
  const { data: reqsData } = useQuery({
    queryKey: ["requisitions"],
    queryFn: () => api.get<RequisitionsResponse>("/requisitions"),
  });

  const scopedReq = reqScoped ? (reqsData?.requisitions ?? []).find((r) => r.id === activeReqId) : undefined;

  const requisitionsList = useMemo(() => {
    return (reqsData?.requisitions ?? []).filter((r) => getRequisitionStatus(r.state) === "Active");
  }, [reqsData]);

  // EVERY candidate in the org, or just one role's when the filter is set.
  //
  // This used to call `GET /requisitions/{id}` and type the reply as a funnel response. That
  // endpoint returns `{ requisition }` and no candidates whatsoever, so `.candidates` was always
  // undefined, the real branch below never ran, and the page rendered twenty-five invented people
  // for every org — including orgs whose database had real candidates in it.
  const { data: candidatesData, isLoading: candidatesLoading } = useQuery({
    queryKey: ["org-candidates", activeReqId],
    queryFn: () =>
      api.get<OrgCandidatesResponse>(
        activeReqId === "all"
          ? "/org/candidates"
          : `/org/candidates?requisitionId=${encodeURIComponent(activeReqId)}`,
      ),
    refetchInterval: 30_000, // candidates arrive by email/connector without a page action
  });

  // Requisition options for the dropdowns
  const reqOptions = useMemo(() => {
    const list = requisitionsList.map((r) => ({
      id: r.id,
      title: displayTitle(r.title),
      count: r.candidateCount ?? 0,
    }));
    return list;
  }, [requisitionsList]);

  /**
   * The candidate list, mapped straight from the API.
   *
   * What this replaces: a branch that mapped real candidacies, then PADDED them with invented
   * people whenever fewer than five came back (`matchingSamples.slice(realItems.length)`) — so a
   * role with two real applicants showed two real rows and three fabricated ones, side by side
   * and indistinguishable. Below, every row corresponds to a candidacy in the database.
   *
   * Fields the API cannot know are left EMPTY rather than invented. The old mapping filled
   * company as "Enterprise Systems", location as "Dallas, TX (Remote)", a fixed four-skill list
   * and `experienceYears: 4 + (i % 4)` — a number derived from the row's position on screen.
   * Those are candidate facts a recruiter might act on, so an empty field is the only honest
   * value until resume parsing supplies a real one.
   */
  const allCandidates = useMemo<CandidateResumeItem[]>(() => {
    const rows = (candidatesData?.candidates ?? []).filter((c) => !funnelStage || reachedStage(c.state, funnelStage.rank));
    return rows.map((c) => {
      const name = c.name?.trim() || "Unnamed candidate";
      const initials =
        name
          .split(/\s+/)
          .map((w) => w[0])
          .join("")
          .slice(0, 2)
          .toUpperCase() || "CD";

      // The recruiter's own tag wins; otherwise derive a stage from the system state.
      const derivedStage: CandidateResumeItem["pipelineStage"] =
        c.interviewScored || c.interviewStarted
          ? "interview"
          : c.state === "advanced" || c.state === "finalist"
            ? "interview"
            : c.state === "screened"
              ? "screening"
              : c.pipelineStatus === "shortlisted"
                ? "shortlisted"
                : c.pipelineStatus === "rejected"
                  ? "rejected"
                  : "matches";
      const taggedStage: CandidateResumeItem["pipelineStage"] | null =
        c.pipelineStatus === "shortlisted"
          ? "shortlisted"
          : c.pipelineStatus === "rejected"
            ? "rejected"
            : null;

      // What the system actually did with this person's CV — not a guess.
      const systemStatus: CandidateResumeItem["systemStatus"] = !c.hasResume
        ? "pending_resume"
        : c.scanScored
          ? "ai_matched"
          : "match_skipped";

      // How they arrived. `source` is only populated from migration 0083 onward, so an older row
      // honestly reports a direct upload rather than claiming a connector it never came through.
      const channel: CandidateResumeItem["channel"] =
        c.source === "email" ? "In-System Email" : "Direct Upload";

      const colors: CandidateResumeItem["avatarColor"][] = ["blue", "yellow", "teal", "purple"];
      // Stable per candidate (not per screen position) so a colour doesn't move when the list
      // re-sorts or a new candidate arrives at the top.
      const colorIdx = c.candidacyId.charCodeAt(0) % colors.length;

      return {
        id: c.candidacyId,
        candidacyId: c.candidacyId,
        name,
        email: c.email ?? "",
        reqId: c.requisitionId,
        reqTitle: displayTitle(c.requisitionTitle),
        channel,
        systemStatus: candidateMatchOverrides[c.candidacyId]?.systemStatus ?? systemStatus,
        pipelineStage: candidateStageOverrides[c.candidacyId] ?? taggedStage ?? derivedStage,
        matchScore:
          candidateMatchOverrides[c.candidacyId] !== undefined
            ? candidateMatchOverrides[c.candidacyId]!.matchScore
            : c.resumeScore,
        isInterviewed: interviewOverride[c.candidacyId] ?? c.interviewScored,
        interviewScore: c.interviewScore ?? undefined,
        rankingBasis: c.rankingBasis,
        interviewRank: c.rankBucket && c.rankBucket !== "not_evaluated" ? c.rank : undefined,
        interviewBucket: c.rankBucket,
        hasResume: c.hasResume,
        // A LIVE persisted request (the bulk import's email, or a Request CV made earlier) counts —
        // this used to be tab-local state only, so it read "Request CV" again after every reload.
        missingResumeEmailSent: Boolean(
          missingResumeInvites[c.candidacyId] ||
            c.cvRequest?.status === "sent" ||
            c.cvRequest?.status === "opened" ||
            (c.source === "email" && !c.hasResume),
        ),
        cvRequest: c.cvRequest ?? null,
        roleTitle: displayTitle(c.requisitionTitle),
        // Unknown until a parsed resume tells us. Empty, never filled with a plausible placeholder.
        company: "",
        experienceYears: 0,
        location: "",
        skills: [],
        resumeFileName: c.resumeFilename ?? undefined,
        scanId: c.scanId,
        scanScored: c.scanScored,
        initials,
        avatarColor: colors[colorIdx]!,
        status:
          candidateStates[c.candidacyId] ||
          (c.state === "advanced" || c.state === "finalist" ? "approved" : "pending"),
        createdAt: c.createdAt,
      };
    });
  }, [
    candidatesData,
    funnelStage?.rank,
    candidateStates,
    candidateStageOverrides,
    candidateMatchOverrides,
    interviewOverride,
    missingResumeInvites,
  ]);

  // Compute dynamic counts for the 5 pipeline stage tabs
  const tabCounts = useMemo(() => {
    const base = allCandidates.filter((cand) => {
      if (activeReqId !== "all" && cand.reqId !== activeReqId) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const nameMatch = cand.name.toLowerCase().includes(q);
        const emailMatch = cand.email.toLowerCase().includes(q);
        const roleMatch = cand.roleTitle.toLowerCase().includes(q);
        const skillMatch = cand.skills.some((s) => s.toLowerCase().includes(q));
        if (!nameMatch && !emailMatch && !roleMatch && !skillMatch) return false;
      }
      if (!passesScoreAndSource(cand, scoreFilter, sourceFilter)) return false;
      if (systemStatusFilter !== "all" && cand.systemStatus !== systemStatusFilter) {
        return false;
      }
      if (selectedSkills.length > 0) {
        const hasSkill = selectedSkills.every((s) =>
          cand.skills.some((cSkill) => cSkill.toLowerCase().includes(s.toLowerCase()))
        );
        if (!hasSkill) return false;
      }
      if (experienceLevel > 0 && cand.experienceYears < experienceLevel) {
        return false;
      }
      return true;
    });

    const all = base.length;
    const matches = base.filter(isMatch).length;
    const shortlisted = base.filter((c) => c.pipelineStage === "shortlisted").length;
    const screening = base.filter((c) => c.pipelineStage === "screening").length;
    const interview = base.filter((c) => c.pipelineStage === "interview" || c.isInterviewed || c.interviewInviteSent).length;

    const cv_requested = base.filter((c) => c.missingResumeEmailSent).length;

    return { all, matches, shortlisted, screening, interview, cv_requested };
  }, [allCandidates, activeReqId, searchQuery, systemStatusFilter, selectedSkills, experienceLevel, scoreFilter, sourceFilter]);

  // Candidates belonging to the currently selected stage tab and active filters
  const tabScopedCandidates = useMemo(() => {
    return allCandidates.filter((cand) => {
      // Requisition filter
      if (activeReqId !== "all" && cand.reqId !== activeReqId) {
        return false;
      }

      // Pipeline Stage Tab filter
      if (pipelineTab === "matches") {
        if (!isMatch(cand)) return false;
      } else if (pipelineTab === "cv_requested") {
        if (!cand.missingResumeEmailSent) return false;
      } else if (pipelineTab === "shortlisted") {
        if (cand.pipelineStage !== "shortlisted") {
          return false;
        }
      } else if (pipelineTab === "screening") {
        if (cand.pipelineStage !== "screening") {
          return false;
        }
      } else if (pipelineTab === "interview") {
        if (cand.pipelineStage !== "interview" && !cand.isInterviewed && !cand.interviewInviteSent) {
          return false;
        }
      }

      // System status filter (from sidebar)
      if (!passesScoreAndSource(cand, scoreFilter, sourceFilter)) return false;
      if (systemStatusFilter !== "all" && cand.systemStatus !== systemStatusFilter) {
        return false;
      }

      // Search filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const nameMatch = cand.name.toLowerCase().includes(q);
        const emailMatch = cand.email.toLowerCase().includes(q);
        const roleMatch = cand.roleTitle.toLowerCase().includes(q);
        const skillMatch = cand.skills.some((s) => s.toLowerCase().includes(q));
        if (!nameMatch && !emailMatch && !roleMatch && !skillMatch) return false;
      }

      // Skills filter
      if (selectedSkills.length > 0) {
        const hasSkill = selectedSkills.every((s) =>
          cand.skills.some((cSkill) => cSkill.toLowerCase().includes(s.toLowerCase()))
        );
        if (!hasSkill) return false;
      }

      // Experience filter
      if (experienceLevel > 0) {
        if (cand.experienceYears < experienceLevel) return false;
      }

      return true;
    });
  }, [allCandidates, activeReqId, pipelineTab, systemStatusFilter, searchQuery, selectedSkills, experienceLevel, scoreFilter, sourceFilter]);

  /**
   * The processing strip's numbers, counted dynamically from the candidates in the currently selected stage tab.
   * Scoped breakdown: Received / Total, With resume, Matched, Interviewed, and Pending Resume.
   */
  const pipelineCounts = useMemo(() => {
    const rows = tabScopedCandidates;
    return {
      received: rows.length,
      withResume: rows.filter((c) => c.hasResume).length,
      matched: rows.filter((c) => c.matchScore !== null || c.systemStatus === "ai_matched" || c.systemStatus === "extracted").length,
      interviewed: rows.filter((c) => c.isInterviewed).length,
      pendingResume: rows.filter((c) => !c.hasResume || c.systemStatus === "pending_resume").length,
    };
  }, [tabScopedCandidates]);

  // Candidates filtered after applying active metric sub-filter chip
  // Sort (the choice is remembered per browser).
  const [candSort, setCandSort] = useState<CandSort>(() => {
    try {
      const v = localStorage.getItem(CAND_SORT_KEY);
      return CAND_SORTS.some((o) => o.key === v) ? (v as CandSort) : "newest";
    } catch {
      return "newest";
    }
  });
  const chooseCandSort = (k: CandSort): void => {
    setCandSort(k);
    setCurrentPage(1);
    try {
      localStorage.setItem(CAND_SORT_KEY, k);
    } catch {
      /* not remembered */
    }
  };

  const filteredCandidates = useMemo(() => {
    const cmp = candidateSorter(candSort);
    if (metricSubFilter === "all") return [...tabScopedCandidates].sort(cmp);
    return tabScopedCandidates.filter((cand) => {
      if (metricSubFilter === "with_resume") {
        return cand.hasResume;
      }
      if (metricSubFilter === "matched") {
        return cand.matchScore !== null || cand.systemStatus === "ai_matched" || cand.systemStatus === "extracted";
      }
      if (metricSubFilter === "interviewed") {
        return cand.isInterviewed;
      }
      if (metricSubFilter === "pending_resume") {
        return !cand.hasResume || cand.systemStatus === "pending_resume";
      }
      return true;
    }).sort(cmp);
  }, [tabScopedCandidates, metricSubFilter, candSort]);

  // Paginated candidates slice (30 resumes per page)
  const totalCandidates = filteredCandidates.length;
  const totalPages = Math.max(1, Math.ceil(totalCandidates / PAGE_SIZE));
  const paginatedCandidates = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredCandidates.slice(start, start + PAGE_SIZE);
  }, [filteredCandidates, currentPage, PAGE_SIZE]);

  // Selected candidates list objects
  const selectedCandidateList = useMemo(() => {
    return allCandidates.filter((c) => selectedIds.has(c.id));
  }, [allCandidates, selectedIds]);

  const selectedNotInterviewedCount = useMemo(() => {
    return selectedCandidateList.filter((c) => !c.isInterviewed).length;
  }, [selectedCandidateList]);

  const isAllFilteredSelected = useMemo(() => {
    if (filteredCandidates.length === 0) return false;
    return filteredCandidates.every((c) => selectedIds.has(c.id));
  }, [filteredCandidates, selectedIds]);

  // Selection actions
  function toggleSelectAll() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (isAllFilteredSelected) {
        filteredCandidates.forEach((c) => next.delete(c.id));
      } else {
        filteredCandidates.forEach((c) => next.add(c.id));
      }
      return next;
    });
  }

  function selectAllNotInterviewed() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredCandidates
        .filter((c) => !c.isInterviewed)
        .forEach((c) => next.add(c.id));
      return next;
    });
  }

  function toggleSelectCandidate(id: string, e?: React.MouseEvent) {
    if (e) e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function removeCandidateFromBatch(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  /** How many filter-panel filters are narrowing the list (the requirement picker counts too). */
  const activeFilterCount =
    (searchQuery.trim() ? 1 : 0) +
    (activeReqId !== "all" && !reqScoped ? 1 : 0) +
    (systemStatusFilter !== "all" ? 1 : 0) +
    (scoreFilter.length > 0 ? 1 : 0) +
    (sourceFilter !== "all" ? 1 : 0) +
    selectedSkills.length +
    (experienceLevel > 0 ? 1 : 0);

  function clearAllFilters() {
    setSearchQuery("");
    setSelectedSkills([]);
    setExperienceLevel(0);
    setSystemStatusFilter("all");
    setScoreFilter([]);
    setSourceFilter("all");
  }

  // Toggle skills in filter
  function toggleSkill(skill: string) {
    setSelectedSkills((prev) =>
      prev.includes(skill) ? prev.filter((s) => s !== skill) : [...prev, skill]
    );
  }

  // Handle stage change directly
  function handleChangeCandidateStage(id: string, newStage: "matches" | "shortlisted" | "screening" | "interview" | "rejected") {
    setCandidateStageOverrides((prev) => ({
      ...prev,
      [id]: newStage,
    }));
  }

  // Run AI Match on demand for a candidate
  /**
   * Run AI matching on a candidate's resume — for real.
   *
   * This used to be `Math.floor(Math.random() * 12) + 87`: a random number between 87 and 98,
   * written into the row and labelled "AI Matched". A fabricated score on a hiring screen is the
   * one thing this product must never produce (docs/GROUND_RULES.md — the engine ranks, a human
   * decides, and every score must be defensible). It now calls the scorer and shows what comes
   * back, including a degraded result, which costs the customer nothing.
   */
  const runAiMatch = useMutation({
    mutationFn: (c: CandidateResumeItem) =>
      api.post<{ scan: { overall: number | null }; charged: number; degraded: boolean }>(
        `/requisitions/${c.reqId}/resumes/${c.scanId}/match`,
        {},
      ),
    onSuccess: (res) => {
      setActionNote(
        res.degraded
          ? "Couldn't score that resume just now — you haven't been charged. Try again shortly."
          : `Matched — ${res.scan.overall}% against the requirement.`,
      );
      void queryClient.invalidateQueries({ queryKey: ["org-candidates"] });
    },
    onError: (err) => addFailed(err, "Couldn't run matching on that resume."),
  });

  function handleRunAIMatch(cand: CandidateResumeItem, e?: React.MouseEvent) {
    if (e) e.stopPropagation();
    if (!cand.scanId) {
      setActionNote("There's no resume on file for this candidate to match against.");
      return;
    }
    if (cand.scanScored) {
      setActionNote("That resume has already been matched — matching runs once per resume.");
      return;
    }
    runAiMatch.mutate(cand);
  }

  // Skip AI Matching and fast-track to screening, shortlisted or interview
  function handleSkipAIMatch(candId: string, targetStage: "shortlisted" | "screening" | "interview" = "screening", e?: React.MouseEvent) {
    if (e) e.stopPropagation();
    setCandidateStageOverrides((prev) => ({
      ...prev,
      [candId]: targetStage,
    }));
    setCandidateMatchOverrides((prev) => ({
      ...prev,
      [candId]: {
        matchScore: null,
        systemStatus: "match_skipped",
      },
    }));
  }

  // Fast-track directly to interview dispatch
  function handleMoveToInterview(candId: string, e?: React.MouseEvent) {
    if (e) e.stopPropagation();
    // No stage override here: the person moves when the server has actually invited them.
    setSelectedIds(new Set([candId]));
    setBatchStep("confirm");
    setBatchModalOpen(true);
  }

  /**
   * Ask the candidate for their details and CV — for real (migration 0085).
   *
   * This used to set a flag on itself and alert "A secure CV upload link has been dispatched".
   * Nothing was dispatched: no token existed, no email was queued, and the flag reset on reload,
   * so a recruiter believed they had chased a candidate who had never been contacted.
   *
   * It now mints a scoped capability token and queues the owner-editable
   * `candidate_info_request` template through the same outbox every other email uses.
   */
  const requestInfo = useMutation({
    mutationFn: (c: CandidateResumeItem) =>
      api.post<{ sentTo: string; expiresAt: string; link: string }>(
        `/candidacies/${c.candidacyId}/request-info`,
        { needsResume: !c.hasResume, needsProfile: true },
      ),
    onSuccess: (res, c) => {
      setMissingResumeInvites((prev) => ({ ...prev, [c.id]: true }));
      setLastCvLink({ id: c.id, name: c.name, link: res.link });
      setLinkCopied(false);
      setPreviewCandidate((prev) => (prev && prev.id === c.id ? { ...prev, missingResumeEmailSent: true } : prev));
      setActionNote(
        `Sent ${c.name} a secure link to ${res.sentTo}. It expires on ${new Date(
          res.expiresAt,
        ).toLocaleDateString()}, and their reply lands on this job automatically.`,
      );
      void queryClient.invalidateQueries({ queryKey: ["org-candidates"] });
      void queryClient.invalidateQueries({ queryKey: ["candidacy-timeline", `/candidacies/${c.candidacyId}/timeline`] });
    },
    onError: (err) =>
      addFailed(
        err,
        "Couldn't send that request. If the candidate has no email address on file, add one first.",
      ),
  });

  function handleSendMissingResumeLink(cand: CandidateResumeItem) {
    if (!cand.candidacyId) {
      setActionNote("That candidate record is incomplete — reload the page and try again.");
      return;
    }
    if (!cand.email) {
      setActionNote(`${cand.name} has no email address on file, so there's nowhere to send the link.`);
      return;
    }
    if (cand.missingResumeEmailSent) {
      setConfirmResend(cand);
      return;
    }
    requestInfo.mutate(cand);
  }

  // Handle Approve / Advance candidate
  function handleApproveCandidate(cand: CandidateResumeItem) {
    setCandidateStates((prev) => ({
      ...prev,
      [cand.id]: prev[cand.id] === "approved" ? "pending" : "approved",
    }));
  }

  /** Reject asks for confirmation; undoing a reject does not. */
  function requestRejectCandidate(cand: CandidateResumeItem) {
    if (cand.status === "rejected") handleRejectCandidate(cand);
    else setConfirmReject(cand);
  }

  // Handle Reject / Archive candidate
  function handleRejectCandidate(cand: CandidateResumeItem) {
    setCandidateStates((prev) => ({
      ...prev,
      [cand.id]: prev[cand.id] === "rejected" ? "pending" : "rejected",
    }));
  }

  // Batch interview dispatch — REAL invites (6 Oct 2026). This used to wait 600 ms, show links made
  // from the candidacy id (not a token — they opened nothing) and flip local state, so nobody was
  // ever emailed. Now: POST /requisitions/{id}/candidates/invite per requirement in the selection.
  // The server mints the capability tokens, emails each candidate, spends one AI interview each and
  // skips (without charging) anyone already invited.
  async function handleBatchDispatch() {
    setIsDispatching(true);
    setDispatchError(null);
    setDispatchSkipped(0);
    const byReq = new Map<string, string[]>();
    for (const c of selectedCandidateList) {
      if (!c.candidacyId) continue;
      byReq.set(c.reqId, [...(byReq.get(c.reqId) ?? []), c.candidacyId]);
    }
    const links: Array<{ id: string; name: string; email: string; link: string }> = [];
    let skipped = 0;
    try {
      for (const [reqId, ids] of byReq) {
        const res = await api.post<{
          invited: { candidacyId: string; name: string; email: string | null; emailed: boolean; inviteUrl: string }[];
          skipped: { candidacyId: string; reason: string }[];
        }>(`/requisitions/${reqId}/candidates/invite`, { candidacyIds: ids });
        for (const a of res.invited) {
          links.push({ id: a.candidacyId, name: a.name, email: a.email ?? "(no email — share the link)", link: a.inviteUrl });
        }
        skipped += res.skipped.length;
      }
      setGeneratedBatchLinks(links);
      setDispatchSkipped(skipped);
      setBatchStep("success");
      void queryClient.invalidateQueries({ queryKey: ["org-candidates"] });
    } catch (err) {
      // Anything invited before the failure (an earlier requirement in the selection) is real —
      // show it rather than pretending the whole batch failed.
      setGeneratedBatchLinks(links);
      setDispatchError(
        err instanceof ApiError && err.status === 402
          ? "Not enough AI interviews left on your plan for this batch — nothing was charged for the ones not sent. Top up and try again."
          : err instanceof Error
            ? err.message
            : "Couldn’t send the interview links — please try again.",
      );
      if (links.length) setBatchStep("success");
      void queryClient.invalidateQueries({ queryKey: ["org-candidates"] });
    } finally {
      setIsDispatching(false);
    }
  }


  function handleCopyAllLinks() {
    const text = generatedBatchLinks
      .map((item) => `${item.name} (${item.email}): ${item.link}`)
      .join("\n");
    navigator.clipboard.writeText(text);
    setCopiedBatchAll(true);
    setTimeout(() => setCopiedBatchAll(false), 2500);
  }

  function handleCopySingleLink(link: string, id: string) {
    navigator.clipboard.writeText(link);
    setCopiedSingleId(id);
    setTimeout(() => setCopiedSingleId(null), 2000);
  }

  /**
   * THE THREE WAYS TO ADD A CANDIDATE — all three now go to the API.
   *
   * Each of these used to build an object, push it onto the module-level sample array and show an
   * alert claiming success. Nothing was persisted: the candidate vanished on reload, never existed
   * for anyone else in the org, and the upload path announced "AI matched" beside a match score
   * of 92 that was a literal in the source.
   */

  /** The requisition these land on. Null when the org has no requisitions — the UI blocks first. */
  const addTargetId =
    uploadTargetReqId || (activeReqId !== "all" ? activeReqId : "") || requisitionsList[0]?.id || null;
  const addTargetTitle =
    requisitionsList.find((r) => r.id === addTargetId)?.title ?? "";

  const openAddModal = (): void => {
    setActionNote(null);
    setUploadModalOpen(true);
  };

  const afterAdd = (message: string): void => {
    setUploadModalOpen(false);
    void queryClient.invalidateQueries({ queryKey: ["org-candidates"] });
    void queryClient.invalidateQueries({ queryKey: ["requisitions"] });
    setActionNote(message);
  };

  const addFailed = (err: unknown, fallback: string): void => {
    // A 402 is the plan-allowance shortfall — say so plainly (the server names what ran out).
    const msg =
      err instanceof ApiError && err.status === 402
        ? serverMessage(err, "Not enough left in your plan for that. Buy more on Plans & usage and try again.")
        : err instanceof ApiError && err.status === 400
          ? "The server rejected that — check the name and email and try again."
          : fallback;
    setActionNote(msg);
  };

  /**
   * Mode 1 — upload CVs (choose or drag & drop; several at once). Each file is its own request to
   * POST /requisitions/{id}/resumes, which stores it, extracts the text and scores it — and each gets
   * its own visible status (CvUploadStatus): reading → uploading % → processing & evaluating →
   * completed with the score, or failed with the server's reason. The modal stays open so the
   * recruiter sees every outcome; nothing disappears silently.
   */
  const [uploads, setUploads] = useState<CvUploadItem[]>([]);
  const [dragging, setDragging] = useState(false);
  const uploadsBusy = uploads.some((u) => u.phase === "reading" || u.phase === "uploading" || u.phase === "processing");
  const patchUpload = (id: string, patch: Partial<CvUploadItem>): void =>
    setUploads((list) => list.map((u) => (u.id === id ? { ...u, ...patch } : u)));

  async function uploadOneCv(id: string, file: File, reqId: string): Promise<void> {
    try {
      const contentBase64 = await fileToBase64(file);
      patchUpload(id, { phase: "uploading", progress: 0 });
      const res = await apiUpload<{ scans: { overall: number | null }[]; scored: number; unscored: number; duplicates: number }>(
        "POST",
        `/requisitions/${reqId}/resumes`,
        { resumes: [{ filename: file.name, mime: file.type || "application/octet-stream", contentBase64 }] },
        {
          onProgress: (f) => patchUpload(id, { progress: f }),
          onUploaded: () => patchUpload(id, { phase: "processing" }),
        },
      );
      if (res.duplicates > 0 && res.scans.length === 0) {
        patchUpload(id, { phase: "done", message: "Already on file for this requirement — not uploaded again, not charged." });
      } else if (res.scored > 0) {
        patchUpload(id, { phase: "done", score: res.scans[0]?.overall ?? null, message: "Stored and evaluated against the requirement." });
      } else {
        patchUpload(id, {
          phase: "failed",
          message: "Stored, but the AI couldn't evaluate it (not charged). Open the candidate and use Run AI match to retry.",
        });
      }
    } catch (err) {
      patchUpload(id, {
        phase: "failed",
        message:
          err instanceof ApiError && err.status === 402
            ? serverMessage(err, "Not enough resumes left in your plan. Buy more on Plans & usage and try again.")
            : err instanceof Error
              ? err.message
              : "Couldn't upload this CV. Please retry.",
      });
    }
  }

  async function handleCvFiles(list: FileList | File[] | null | undefined): Promise<void> {
    const files = Array.from(list ?? []);
    if (files.length === 0) return;
    if (!addTargetId) {
      setActionNote("Create a job requirement first — a candidate has to belong to a role.");
      return;
    }
    const reqId = addTargetId;
    const queued = files.map((f) => ({ file: f, item: { id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, name: f.name, phase: "reading" as const, progress: 0 } }));
    setUploads((cur) => [...queued.map((q) => q.item), ...cur]);
    // One at a time: each file's status stays accurate and a 20-CV drop doesn't fire 20 model calls at once.
    for (const q of queued) {
      const problem = cvProblem(q.file);
      if (problem) {
        patchUpload(q.item.id, { phase: "failed", message: problem });
        continue;
      }
      await uploadOneCv(q.item.id, q.file, reqId);
    }
    void queryClient.invalidateQueries({ queryKey: ["org-candidates"] });
    void queryClient.invalidateQueries({ queryKey: ["requisitions"] });
  }

  function handleFileUpload(e: ChangeEvent<HTMLInputElement>) {
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = ""; // let the same file be picked again after a failure
    void handleCvFiles(files);
  }

  /**
   * Modes 2 and 3 both create a candidacy via POST /requisitions/{id}/candidates — the same
   * endpoint, because they are the same act: a person the recruiter is putting on a role. What
   * differs is what happens next, and that is the recruiter's choice, not two code paths.
   */
  const addCandidate = useMutation({
    // The response key is `added`, not `candidates` — the request sends `candidates`, the reply
    // names what it created. A candidate already invited to this role is skipped server-side
    // (the unique index on (requisition_id, persona_id)), so `added` can legitimately be empty.
    mutationFn: (v: { name: string; email: string }) =>
      api.post<{ added: { candidacyId: string; emailed?: boolean }[]; creditsSpent?: number }>(
        `/requisitions/${addTargetId}/candidates`,
        { candidates: [{ name: v.name, email: v.email }] },
      ),
  });

  function handleDirectAddSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!directName.trim() || !directEmail.trim()) {
      setActionNote("Please provide both candidate name and email.");
      return;
    }
    if (!addTargetId) {
      setActionNote("Create a job requirement first — a candidate has to belong to a role.");
      return;
    }
    addCandidate.mutate(
      { name: directName.trim(), email: directEmail.trim() },
      {
        onSuccess: (res) => {
          const created = res.added?.[0];
          // The recruiter picked a stage on the form; apply it as the pipeline TAG (0077).
          // A label, never a decision — it does not advance or reject anybody.
          if (created?.candidacyId && directStage !== "interview") {
            void apiFetch(`/requisitions/${addTargetId}/candidates/${created.candidacyId}`, {
              method: "PATCH",
              body: { pipelineStatus: directStage === "screening" ? "needs_review" : "shortlisted" },
            }).catch(() => undefined);
          }
          setDirectName("");
          setDirectEmail("");
          setDirectRole("");
          afterAdd(
            created
              ? `${directName.trim()} added to ${displayTitle(addTargetTitle)}${created.emailed ? " and sent their interview link" : ""
              }. No AI matching was run.`
              : `${directName.trim()} is already on ${displayTitle(addTargetTitle)} — nothing was charged.`,
          );
        },
        onError: (err) => addFailed(err, "Couldn't add that candidate. Please retry."),
      },
    );
  }

  function handleRequestResumeSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!requestName.trim() || !requestEmail.trim()) {
      setActionNote("Please provide candidate name and email.");
      return;
    }
    if (!addTargetId) {
      setActionNote("Create a job requirement first — a candidate has to belong to a role.");
      return;
    }
    addCandidate.mutate(
      { name: requestName.trim(), email: requestEmail.trim() },
      {
        onSuccess: (res) => {
          const created = res.added?.[0];
          setMissingResumeInvites((m) => ({ ...m, [created?.candidacyId ?? ""]: true }));
          setRequestName("");
          setRequestEmail("");
          setRequestCustomNote("");
          // Says exactly what was sent. The old copy claimed "a CV upload invitation link has
          // been dispatched"; what this endpoint sends is the candidate's interview link.
          afterAdd(
            !created
              ? `${requestName.trim()} is already on this role — nothing was charged.`
              : created.emailed
                ? `${requestName.trim()} added and emailed their candidate link.`
                : `${requestName.trim()} added. No email was sent — copy their link from the requirement page.`,
          );
        },
        onError: (err) => addFailed(err, "Couldn't create that candidate. Please retry."),
      },
    );
  }

  /** The pipeline-stage tag control (a label the recruiter sets — never a decision). */
  const renderStageControl = (cand: CandidateResumeItem): ReactNode => {
    const stageCfg = STAGE_CONFIG[cand.pipelineStage as keyof typeof STAGE_CONFIG] || STAGE_CONFIG.matches;
    const isOpen = openStageDropdown === cand.id;
    return (
      <div className="t1c-stage" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="t1c-stage-btn"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onClick={() => {
            setRowMenu(null);
            setOpenStageDropdown(isOpen ? null : cand.id);
          }}
          title="Change pipeline stage (a label — not a decision)"
        >
          <i style={{ background: stageCfg.dotColor }} aria-hidden="true" />
          <span>{stageCfg.label}</span>
          <ChevronDown style={{ width: 12, height: 12 }} />
        </button>

        {isOpen && (
          <div className="t1c-menu" role="menu">
            {(["matches", "shortlisted", "screening", "interview"] as const).map((stg) => {
              const cfg = STAGE_CONFIG[stg];
              const isSelected = cand.pipelineStage === stg;
              return (
                <button
                  key={stg}
                  type="button"
                  role="menuitemradio"
                  aria-checked={isSelected}
                  className={isSelected ? "on" : ""}
                  onClick={() => {
                    handleChangeCandidateStage(cand.id, stg);
                    setOpenStageDropdown(null);
                  }}
                >
                  <i style={{ background: cfg.dotColor }} aria-hidden="true" />
                  <span>{cfg.label}</span>
                  {isSelected && <Check style={{ width: 13, height: 13, marginLeft: "auto" }} />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Resend: a fresh link (the earlier one stops working), emailed again and shown with Copy link.
  // Not billed — the interview was paid for when it was first sent (POST /candidacies/{id}/interview-link).
  const resendInterview = useMutation({
    mutationFn: (c: CandidateResumeItem) =>
      api.post<{ inviteUrl: string; expiresAt: string; emailedTo: string | null }>(
        `/candidacies/${c.candidacyId}/interview-link`,
        { email: true },
      ),
    onSuccess: (res, c) => {
      setLastCvLink({ id: c.id, name: c.name, link: res.inviteUrl });
      setLinkCopied(false);
      setActionNote(
        res.emailedTo
          ? `Re-sent ${c.name}'s interview link to ${res.emailedTo}. The earlier link no longer works.`
          : `New interview link for ${c.name} — there's no email on file, so copy it and share it with them.`,
      );
    },
    onError: (err, c) =>
      setActionNote(err instanceof ApiError ? err.message : `Couldn't resend ${c.name}'s interview link.`),
  });

  /** The one primary action a row shows, chosen by where the candidate is. */
  const renderPrimaryAction = (cand: CandidateResumeItem): ReactNode => {
    if (cand.systemStatus === "pending_resume") {
      return (
        <button
          type="button"
          className="btn sm"
          onClick={() => handleSendMissingResumeLink(cand)}
          title={cand.missingResumeEmailSent ? "Send the CV upload link again" : "Send the candidate a link to upload their CV"}
          disabled={requestInfo.isPending && requestInfo.variables?.id === cand.id}
        >
          {cand.missingResumeEmailSent ? <Check style={{ width: 13, height: 13 }} /> : <Mail style={{ width: 13, height: 13 }} />}
          <span>{cand.missingResumeEmailSent ? "CV requested" : "Request CV"}</span>
        </button>
      );
    }
    if (cand.systemStatus === "extracted" || cand.systemStatus === "match_skipped") {
      return (
        <button
          type="button"
          className="btn sm"
          onClick={(e) => handleRunAIMatch(cand, e)}
          title="Score this resume against the job requirement"
          disabled={runAiMatch.isPending && runAiMatch.variables?.id === cand.id}
        >
          <span>{runAiMatch.isPending && runAiMatch.variables?.id === cand.id ? "Matching…" : "Run AI match"}</span>
        </button>
      );
    }
    // Interviewed: the row shows both scores and opens the review page itself — no extra button.
    if (cand.isInterviewed) return null;
    if (cand.interviewInviteSent && cand.candidacyId) {
      const busy = resendInterview.isPending && resendInterview.variables?.id === cand.id;
      return (
        <button
          type="button"
          className="btn sm"
          onClick={(e) => {
            e.stopPropagation();
            resendInterview.mutate(cand);
          }}
          disabled={busy}
          title="Email the interview link again (a fresh link — the earlier one stops working). Not charged again."
        >
          <Send style={{ width: 13, height: 13 }} />
          <span>{busy ? "Resending…" : "Resend interview"}</span>
        </button>
      );
    }
    return (
      <button
        type="button"
        className="btn sm"
        onClick={(e) => handleMoveToInterview(cand.id, e)}
        title="Send a personalised voice AI interview link"
      >
        <Send style={{ width: 13, height: 13 }} />
        <span>Send interview</span>
      </button>
    );
  };

  /** Secondary row actions behind "…": preview, the extra workflow steps, approve and reject. */
  const renderRowMenu = (cand: CandidateResumeItem): ReactNode => {
    const isOpen = rowMenu === cand.id;
    const isApproved = cand.status === "approved";
    const isRejected = cand.status === "rejected";
    const close = (): void => setRowMenu(null);
    return (
      <div className="t1c-more" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="t1c-icon-btn"
          aria-label={`More actions for ${cand.name}`}
          data-tip="More actions"
          data-tip-pos="left"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onClick={() => {
            setOpenStageDropdown(null);
            setRowMenu(isOpen ? null : cand.id);
          }}
        >
          <MoreIcon />
        </button>
        {isOpen && (
          <div className="t1c-menu right" role="menu">
            <button type="button" role="menuitem" onClick={() => { close(); setPreviewCandidate(cand); }}>
              <Info style={{ width: 14, height: 14 }} />
              <span>View activity</span>
            </button>
            {cand.candidacyId && !cand.isInterviewed && (
              <button type="button" role="menuitem" onClick={() => { close(); handleSendMissingResumeLink(cand); }}>
                <Mail style={{ width: 14, height: 14 }} />
                <span>{cand.missingResumeEmailSent ? "Request CV again" : "Request CV"}</span>
              </button>
            )}
            {cand.systemStatus === "extracted" && (
              <button type="button" role="menuitem" onClick={(e) => { close(); handleSkipAIMatch(cand.id, "screening", e); }}>
                <ArrowRightIcon />
                <span>Skip matching</span>
              </button>
            )}
            {(cand.systemStatus === "extracted" || cand.systemStatus === "match_skipped") && !cand.isInterviewed && (
              <button type="button" role="menuitem" onClick={(e) => { close(); handleMoveToInterview(cand.id, e); }}>
                <Send style={{ width: 14, height: 14 }} />
                <span>Send interview</span>
              </button>
            )}
            <div className="t1c-menu-sep" role="separator" />
            <div className="t1c-menu-h">Stage (a label, not a decision)</div>
            {(["matches", "shortlisted", "screening", "interview"] as const).map((stg) => {
              const cfg = STAGE_CONFIG[stg];
              const on = cand.pipelineStage === stg;
              return (
                <button
                  key={stg}
                  type="button"
                  role="menuitemradio"
                  aria-checked={on}
                  className={on ? "on" : ""}
                  onClick={() => { close(); handleChangeCandidateStage(cand.id, stg); }}
                >
                  <i style={{ background: cfg.dotColor }} aria-hidden="true" />
                  <span>{cfg.label}</span>
                  {on && <Check style={{ width: 13, height: 13, marginLeft: "auto" }} />}
                </button>
              );
            })}
            <div className="t1c-menu-sep" role="separator" />
            <button type="button" role="menuitem" onClick={() => { close(); handleApproveCandidate(cand); }}>
              <Check style={{ width: 14, height: 14 }} />
              <span>{isApproved ? "Clear approval" : "Mark as approved"}</span>
            </button>
            <div className="t1c-menu-sep" role="separator" />
            <button
              type="button"
              role="menuitem"
              className={isRejected ? "" : "danger"}
              onClick={() => { close(); requestRejectCandidate(cand); }}
            >
              <X style={{ width: 14, height: 14 }} />
              <span>{isRejected ? "Undo reject" : "Reject…"}</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  // #239: with no candidates there is nothing to filter — the Filters panel and its button stay
  // hidden until the first candidate arrives (an empty panel of controls reads as broken).
  const showFilters = !candidatesLoading && allCandidates.length > 0;

  // Switch requirement filter
  function handleSelectRequirement(newReqId: string) {
    if (newReqId === "all") {
      params.delete("reqId");
      params.delete("stage"); // a funnel stage belongs to one requirement's funnel
      setParams(params);
    } else {
      setParams({ reqId: newReqId });
    }
  }

  return (
    <div className="t1c">
      {/* ── Page header (standard .page-h, audit X1) ─────────────────────────── */}
      <div className="page-h t1c-head">
        <div style={{ minWidth: 0 }}>
          {reqScoped ? (
            <>
              <Link to={`/app/requisitions/${activeReqId}`} className="t1c-req-back">
                ← Requirement
              </Link>
              <h1 className="t1c-req-h1" title={scopedReq?.title}>
                {scopedReq ? displayTitle(scopedReq.title) : "Candidates"}
              </h1>
              <div className="sub">Candidates for this requirement. Select several to send voice AI interview links in one go.</div>
            </>
          ) : (
            <>
              <h1>Candidates</h1>
              <div className="sub">
                Every candidate across your job requirements. Select several to send voice AI interview links in one go.
              </div>
            </>
          )}
          {funnelStage && <FunnelStageChip label={funnelStage.label} count={allCandidates.length} />}
        </div>
        <div className="t1c-head-actions">
          <button type="button" className="btn" onClick={() => setConnectModalOpen(true)}>
            <Link2 style={{ width: 15, height: 15 }} />
            <span>Connect</span>
          </button>
          <button type="button" className="btn primary" onClick={openAddModal}>
            <Plus style={{ width: 15, height: 15 }} />
            <span>Add candidates</span>
          </button>
        </div>
      </div>

      {/* ── KPI cards (first one dark). Each narrows the list below, like the old metric chips. ── */}
      <section className="t1c-kpis" aria-label="Candidate summary">
        <button
          type="button"
          className="t1c-kpi dark"
          onClick={() => setMetricSubFilter("all")}
          aria-pressed={metricSubFilter === "all"}
        >
          <span className="t1c-kpi-top">
            Candidates
            <span className="t1c-kpi-ico" aria-hidden="true"><Users /></span>
          </span>
          <span className="t1c-kpi-val tnum">{pipelineCounts.received}</span>
          <span className="t1c-kpi-sub">in this view · {allCandidates.length} in total</span>
        </button>
        <button
          type="button"
          className={`t1c-kpi ${metricSubFilter === "with_resume" ? "on" : ""}`}
          onClick={() => setMetricSubFilter((prev) => (prev === "with_resume" ? "all" : "with_resume"))}
          aria-pressed={metricSubFilter === "with_resume"}
        >
          <span className="t1c-kpi-top">
            With resume
            <span className="t1c-kpi-ico" aria-hidden="true"><FileText /></span>
          </span>
          <span className="t1c-kpi-val tnum">{pipelineCounts.withResume}</span>
          <span className="t1c-kpi-sub">{pipelineCounts.pendingResume} still waiting for a CV</span>
        </button>
        <button
          type="button"
          className={`t1c-kpi ${metricSubFilter === "matched" ? "on" : ""}`}
          onClick={() => setMetricSubFilter((prev) => (prev === "matched" ? "all" : "matched"))}
          aria-pressed={metricSubFilter === "matched"}
        >
          <span className="t1c-kpi-top">
            AI matched
            <span className="t1c-kpi-ico" aria-hidden="true"><Search /></span>
          </span>
          <span className="t1c-kpi-val tnum">{pipelineCounts.matched}</span>
          <span className="t1c-kpi-sub">resumes scored against the job</span>
        </button>
        <button
          type="button"
          className={`t1c-kpi ${metricSubFilter === "interviewed" ? "on" : ""}`}
          onClick={() => setMetricSubFilter((prev) => (prev === "interviewed" ? "all" : "interviewed"))}
          aria-pressed={metricSubFilter === "interviewed"}
        >
          <span className="t1c-kpi-top">
            Interviewed
            <span className="t1c-kpi-ico" aria-hidden="true"><Send /></span>
          </span>
          <span className="t1c-kpi-val tnum">{pipelineCounts.interviewed}</span>
          <span className="t1c-kpi-sub">AI interview completed</span>
        </button>
      </section>

      <div className={`t1c-layout ${showFilters && sidebarOpen ? "" : "no-filters"}`}>
        {/* ── Left filter panel. On phones it is a drawer opened by the Filters button. ── */}
        {showFilters && sidebarOpen && (
          <button
            type="button"
            className="t1c-drawer-scrim"
            aria-label="Close filters"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        {showFilters && sidebarOpen && (
          <aside id="t1c-filters" className="card t1c-filters" aria-label="Filter candidates">
            <div className="t1c-filters-h">
              <span>
                <Filter style={{ width: 15, height: 15 }} />
                Filters
                {activeFilterCount > 0 && <span className="t1c-count">{activeFilterCount}</span>}
              </span>
              <button
                type="button"
                className="t1c-icon-btn"
                onClick={() => setSidebarOpen(false)}
                aria-label="Hide filters"
                data-tip="Hide filters"
                data-tip-pos="left"
              >
                <X style={{ width: 15, height: 15 }} />
              </button>
            </div>

            <div className="t1c-fgroup">
              <label className="t1c-flabel" htmlFor="t1c-search">Search</label>
              <div className="t1c-search">
                <Search aria-hidden="true" />
                <input
                  id="t1c-search"
                  type="search"
                  placeholder="Name, email or role"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {!reqScoped && (
            <div className="t1c-fgroup">
              <span className="t1c-flabel">Requirement</span>
              <RequirementCombobox
                className="t1c-req-combo"
                options={reqOptions.map((req) => ({
                  id: req.id,
                  title: req.title,
                  meta: `${req.count} candidate${req.count === 1 ? "" : "s"}`,
                }))}
                allOption={{ id: "all", title: `All requirements (${allCandidates.length})` }}
                value={activeReqId}
                onSelect={handleSelectRequirement}
              />
            </div>
            )}

            <fieldset className="t1c-fgroup">
              <legend className="t1c-flabel">Status</legend>
              {[
                { label: "All statuses", value: "all" },
                { label: "AI matched & scored", value: "ai_matched" },
                { label: "Not matched yet", value: "match_skipped" },
                { label: "Extracted (unscored)", value: "extracted" },
                { label: "Waiting for CV", value: "pending_resume" },
              ].map((opt) => (
                <label key={opt.value} className="t1c-radio">
                  <input
                    type="radio"
                    name="systemStatusFilter"
                    checked={systemStatusFilter === opt.value}
                    onChange={() => setSystemStatusFilter(opt.value as typeof systemStatusFilter)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </fieldset>

            <fieldset className="t1c-fgroup">
              <legend className="t1c-flabel">Resume match</legend>
              {(
                [
                  { label: "Any score", value: "any" },
                  { label: "Strong (70%+)", value: "hi" },
                  { label: "Partial (50–69%)", value: "mid" },
                  { label: "Weak (below 50%)", value: "low" },
                  { label: "Not scored yet", value: "unscored" },
                ] as const
              ).map((opt) => (
                <label key={opt.value} className="t1c-radio">
                  <input
                    type="checkbox"
                    checked={opt.value === "any" ? scoreFilter.length === 0 : scoreFilter.includes(opt.value)}
                    onChange={() => {
                      const v = opt.value;
                      if (v === "any") setScoreFilter([]);
                      else setScoreFilter((cur) => (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]));
                    }}
                  />
                  <span>{opt.label}</span>
                  {(opt.value === "hi" || opt.value === "mid" || opt.value === "low") && (
                    <i className={`t1c-band ${opt.value}`} aria-hidden="true" />
                  )}
                </label>
              ))}
            </fieldset>

            <fieldset className="t1c-fgroup">
              <legend className="t1c-flabel">Source</legend>
              {(
                [
                  { label: "All sources", value: "all" },
                  { label: "Email applications", value: "email" },
                  { label: "Direct upload / added", value: "direct" },
                ] as const
              ).map((opt) => (
                <label key={opt.value} className="t1c-radio">
                  <input
                    type="radio"
                    name="sourceFilter"
                    checked={sourceFilter === opt.value}
                    onChange={() => setSourceFilter(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </fieldset>

            <details className="t1c-fgroup t1c-more-filters" open={selectedSkills.length > 0 || experienceLevel > 0}>
              <summary className="t1c-flabel">Skills &amp; experience</summary>
              <div className="t1c-chips">
                {SKILL_FILTER_CHIPS.map((skill) => {
                  const isSelected = selectedSkills.includes(skill);
                  return (
                    <button
                      key={skill}
                      type="button"
                      className={`t1c-chip ${isSelected ? "on" : ""}`}
                      aria-pressed={isSelected}
                      onClick={() => toggleSkill(skill)}
                    >
                      {skill}
                    </button>
                  );
                })}
              </div>
              {[
                { label: "Any experience", value: 0 },
                { label: "3+ years", value: 3 },
                { label: "5+ years", value: 5 },
                { label: "7+ years", value: 7 },
              ].map((opt) => (
                <label key={opt.value} className="t1c-radio">
                  <input
                    type="radio"
                    name="experienceLevel"
                    checked={experienceLevel === opt.value}
                    onChange={() => setExperienceLevel(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </details>

            {activeFilterCount > 0 && (
              <button type="button" className="btn sm ghost t1c-clear" onClick={clearAllFilters}>
                <X style={{ width: 14, height: 14 }} />
                Clear all filters
              </button>
            )}
            <button type="button" className="btn primary t1c-drawer-done" onClick={() => setSidebarOpen(false)}>
              Show {filteredCandidates.length} candidate{filteredCandidates.length === 1 ? "" : "s"}
            </button>
          </aside>
        )}

        {/* ── List card: stage tabs, toolbar, compact table ───────────────────── */}
        <section className="card t1c-list">
          <div className="t1c-toolbar">
            <div className="seg t1c-tabs" role="tablist" aria-label="Pipeline stage">
              {(
                [
                  { key: "all", label: "All" },
                  { key: "matches", label: "Matches" },
                  { key: "shortlisted", label: "Shortlisted" },
                  { key: "screening", label: "Screening" },
                  { key: "interview", label: "Interview" },
                  { key: "cv_requested", label: "CV requested" },
                ] as const
              ).map((t) => (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={pipelineTab === t.key}
                  className={`${pipelineTab === t.key ? "on" : ""}${tabCounts[t.key] === 0 ? " zero" : ""}`}
                  onClick={() => {
                    setPipelineTab(t.key);
                    setMetricSubFilter("all");
                  }}
                >
                  {t.label}
                  <i className="tnum">{tabCounts[t.key]}</i>
                </button>
              ))}
            </div>
            <div className="t1c-tools">
              {showFilters && (
              <button
                type="button"
                className={`btn sm t1c-filters-btn ${sidebarOpen ? "t1c-on" : ""}`}
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-expanded={sidebarOpen}
                aria-controls="t1c-filters"
                aria-label={sidebarOpen ? "Hide filters" : "Show filters"}
                data-tip={sidebarOpen ? "Hide filters" : "Show filters"}
              >
                <Filter style={{ width: 15, height: 15 }} />
                <span className="t1c-filters-btn-label">Filters</span>
                {activeFilterCount > 0 && <span className="t1c-count">{activeFilterCount}</span>}
              </button>
              )}
              <label className="t1c-sort">
                <span className="t1c-sr">Sort</span>
                <select value={candSort} onChange={(e) => chooseCandSort(e.target.value as CandSort)} aria-label="Sort candidates">
                  {CAND_SORTS.map((o) => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                  ))}
                </select>
              </label>
              <label className="t1-switch t1c-switch">
                <input
                  type="checkbox"
                  checked={density === "cards"}
                  onChange={(e) => chooseDensity(e.target.checked ? "cards" : "table")}
                />
                <span className="track" />
                <span>Card view</span>
              </label>
            </div>
          </div>

          {/* Selection helpers + the metric sub-filter that a KPI card applied. */}
          <div className="t1c-selrow">
            <label className="t1c-selall">
              <input
                type="checkbox"
                className="candidate-custom-checkbox"
                checked={isAllFilteredSelected}
                onChange={toggleSelectAll}
              />
              <span>Select all ({filteredCandidates.length})</span>
            </label>
            {filteredCandidates.some((c) => !c.isInterviewed) && (
              <button type="button" className="btn sm ghost" onClick={selectAllNotInterviewed}>
                Select not interviewed
              </button>
            )}
            {metricSubFilter !== "all" && (
              <span className="t1c-subfilter" role="status">
                Showing <b>{filteredCandidates.length}</b>{" "}
                {metricSubFilter === "with_resume"
                  ? "with a resume"
                  : metricSubFilter === "matched"
                    ? "AI matched"
                    : metricSubFilter === "interviewed"
                      ? "interviewed"
                      : "waiting for a CV"}
                <button type="button" className="btn sm ghost" onClick={() => setMetricSubFilter("all")}>
                  <X style={{ width: 13, height: 13 }} /> Clear
                </button>
              </span>
            )}
          </div>

          {/* The result of the last add/upload — real server feedback, replacing alert(). */}
          {actionNote && (
            <div className="banner info" style={{ marginBottom: 12 }}>
              <Info />
              <div>
                {actionNote}
                {lastCvLink && (
                  <button className="btn sm" style={{ marginLeft: 10 }} onClick={() => copyCvLink(lastCvLink.link)}>
                    {linkCopied ? "Copied" : "Copy link"}
                  </button>
                )}
                <button
                  className="btn sm ghost"
                  style={{ marginLeft: 10 }}
                  onClick={() => {
                    setActionNote(null);
                    setLastCvLink(null);
                  }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Compact table (default) — checkbox · candidate · requirement · source · status + reason ·
              resume-match ring · one action (+ "…"). Audit 16 / top-15 #6, X3, X10, X11. */}
          {density === "table" && paginatedCandidates.length > 0 && (
            <div className="t1c-tbl-wrap">
              <table className="t1c-table">
                <thead>
                  <tr>
                    <th className="c-sel">
                      <input
                        type="checkbox"
                        className="candidate-custom-checkbox"
                        checked={isAllFilteredSelected}
                        onChange={toggleSelectAll}
                        aria-label="Select all candidates in this view"
                      />
                    </th>
                    <th className="c-who">Candidate</th>
                    <th className="c-req">{reqScoped ? "Resume" : "Requirement"}</th>
                    <th className="c-src">Source</th>
                    <th className="c-status">Status</th>
                    <th className="c-score">Resume</th>
                    <th className="c-act"><span className="t1c-sr">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCandidates.map((cand) => {
                    const isSelected = selectedIds.has(cand.id);
                    const st = candidateStatus(cand);
                    const rowCls = [isSelected ? "selected" : "", cand.status === "rejected" ? "rejected" : ""]
                      .filter(Boolean)
                      .join(" ");
                    const hasMatch = cand.systemStatus === "ai_matched" && cand.matchScore != null;
                    const isEmail = cand.channel === "In-System Email";
                    return (
                      <tr
                        key={cand.id}
                        className={`${rowCls} t1c-row-click`}
                        onClick={(e) => openCandidateFromRow(e, `/app/review/${cand.candidacyId ?? cand.id}`, navigate)}
                      >
                        <td className="c-sel" onClick={(e) => toggleSelectCandidate(cand.id, e)}>
                          <input
                            type="checkbox"
                            className="candidate-custom-checkbox"
                            checked={isSelected}
                            onChange={() => { }}
                            aria-label={`Select ${cand.name}`}
                          />
                        </td>
                        <td className="c-who">
                          <div className="t1c-who">
                            <div className="t1c-av" aria-hidden="true">{cand.initials}</div>
                            <div className="t1c-who-text">
                              {/* The name opens the candidate's full detail (#239); the activity panel lives in "…" → View activity. */}
                              <Link
                                to={`/app/review/${cand.candidacyId ?? cand.id}`}
                                className="t1c-ell t1c-name-link"
                                title={`Open ${cand.name}'s report`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <b>{cand.name}</b>
                              </Link>
                              <small className="t1c-ell" title={cand.email || undefined}>
                                {cand.email || "No email on file"}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td className="c-req">
                          {!reqScoped && <div className="t1c-ell t1c-req-title" title={cand.reqTitle}>{cand.reqTitle}</div>}
                          <div className="t1c-req-sub">
                            {cand.hasResume ? (
                              <button
                                type="button"
                                className="t1c-file"
                                title={cand.resumeFileName ? `Open ${cand.resumeFileName} in a new tab` : "Open the resume in a new tab"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!cand.scanId) {
                                    setPreviewCandidate(cand);
                                    return;
                                  }
                                  void openResumeInTab(cand.reqId, cand.scanId).then((err) => {
                                    if (err) setActionNote(err);
                                  });
                                }}
                              >
                                <FileText style={{ width: 13, height: 13 }} />
                                <span>{cand.resumeFileName || "Resume on file"}</span>
                              </button>
                            ) : (
                              <span className="t1c-nocv">No CV yet</span>
                            )}
                            {cand.createdAt && (
                              <span className="t1c-added">Added {relativeActivityDate(cand.createdAt)}</span>
                            )}
                            <span className="t1c-src-inline">{isEmail ? "via email" : cand.channel}</span>
                          </div>
                        </td>
                        <td className="c-src">
                          <span className="t1c-source" title={`Source: ${cand.channel}`}>
                            <i className={isEmail ? "email" : ""} aria-hidden="true" />
                            {isEmail ? "Email" : cand.channel === "Direct Upload" ? "Upload" : cand.channel === "Direct HR Add" ? "Added by HR" : cand.channel}
                          </span>
                        </td>
                        <td className="c-status">
                          <span className={`pill ${st.tone}`}>{st.label}</span>
                          <div className="t1c-why t1c-ell" title={st.reason}>{st.reason}</div>
                        </td>
                        <td className="c-score">
                          {/* Resume fit, and — once interviewed — the interview result beside it, same ring. */}
                          <div className={`t1c-scores${cand.isInterviewed ? " two" : ""}`}>
                            <span className="t1c-score">
                              {hasMatch ? (
                                <ScoreRing score={cand.matchScore!} label="Resume match" />
                              ) : (
                                <span
                                  className="t1c-ring none"
                                  role="img"
                                  aria-label={cand.hasResume ? "Not matched yet" : "No resume to match"}
                                >
                                  <span>—</span>
                                </span>
                              )}
                              {cand.isInterviewed && <small>Resume</small>}
                            </span>
                            {cand.isInterviewed && (
                              <span className="t1c-score">
                                {cand.interviewScore != null ? (
                                  <ScoreRing score={cand.interviewScore} label="Interview score" />
                                ) : (
                                  <span className="t1c-ring none" role="img" aria-label="Interview not scored yet">
                                    <span>—</span>
                                  </span>
                                )}
                                <small>Interview</small>
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="c-act" onClick={(e) => e.stopPropagation()}>
                          <div className="t1c-act">
                            {renderPrimaryAction(cand)}
                            {renderRowMenu(cand)}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Candidate Cards Feed (Paginated 30 per page) */}
          {density === "cards" && paginatedCandidates.map((cand) => {
            const isApproved = cand.status === "approved";
            const isRejected = cand.status === "rejected";
            const isSelected = selectedIds.has(cand.id);

            return (
              <div
                key={cand.id}
                className={`candidate-resume-card t1c-row-click ${isSelected ? "selected" : ""}`}
                onClick={(e) => openCandidateFromRow(e, `/app/review/${cand.candidacyId ?? cand.id}`, navigate)}
                style={{
                  opacity: isRejected ? 0.6 : 1,
                  background: isSelected ? "var(--teal-wash)" : isApproved ? "var(--surface-2)" : "var(--surface)",
                }}
              >
                <div className="candidate-card-top">
                  <div className="candidate-main-info">
                    {/* Multi-select Checkbox */}
                    <div
                      className="candidate-checkbox-wrap"
                      onClick={(e) => toggleSelectCandidate(cand.id, e)}
                    >
                      <input
                        type="checkbox"
                        className="candidate-custom-checkbox"
                        checked={isSelected}
                        onChange={() => { }}
                        aria-label={`Select ${cand.name}`}
                      />
                    </div>

                    {/* Avatar Initials Circle */}
                    <div className={`candidate-avatar ${cand.avatarColor}`}>
                      {cand.initials}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Name Row */}
                      <div className="candidate-header-pills" style={{ flexWrap: "wrap", rowGap: 6, marginBottom: 2 }}>
                        <Link
                          to={`/app/review/${cand.candidacyId ?? cand.id}`}
                          className="candidate-name t1c-name-link"
                          title={`Open ${cand.name}'s report`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {cand.name}
                        </Link>
                      </div>

                      {/* Job/requirement — the candidate's secondary heading, shown exactly once (not on a
                          one-requirement page, where it is the page heading). */}
                      {!reqScoped && (
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ink-soft)", margin: "0 0 6px" }}>
                          {cand.reqTitle}
                        </div>
                      )}

                      {/* Source / status badge row — kept visually distinct from recruitment status,
                          which lives on the stage control in the actions row (see item 9). */}
                      <div className="candidate-header-pills" style={{ flexWrap: "wrap", rowGap: 6, marginBottom: 6 }}>
                        <SourceBadge channel={cand.channel} />

                        {/* System Processing Status Badge — the match SCORE itself now renders as
                            the ring on the right; this row only carries states with no number. */}
                        {cand.systemStatus === "extracted" && (
                          <span className="pill n">
                            Extracted
                          </span>
                        )}
                        {cand.systemStatus === "match_skipped" && (
                          <span className="pill n">
                            Not matched yet
                          </span>
                        )}

                        {/* Interview Status Badge — never a fabricated score: only shown when the
                            judge actually returned one. */}
                        {cand.isInterviewed ? (
                          <span className="pill t">
                            Interviewed{cand.interviewBucket && cand.interviewBucket !== "not_evaluated" ? ` · ${rankBucketLabel(cand.interviewBucket, cand.rankingBasis ?? "interview")}` : ""}{cand.interviewScore != null ? ` · ${cand.interviewScore}` : ""}
                          </span>
                        ) : (
                          <span className="t1c-muted-text">
                            Not interviewed
                          </span>
                        )}
                      </div>

                      {/* Subtitle: email + when added (relative — Now/Today/Yesterday/date) */}
                      <div className="candidate-summary-line">
                        {[
                          cand.company,
                          cand.experienceYears > 0 ? `${cand.experienceYears} yrs exp` : "",
                          cand.location,
                          cand.createdAt ? `Added ${relativeActivityDate(cand.createdAt)}` : "",
                        ]
                          .filter(Boolean)
                          .map((part, i) => (
                            <span key={i}>
                              {i > 0 && <span> • </span>}
                              <span>{part}</span>
                            </span>
                          ))}
                        {cand.email && (
                          <>
                            {Boolean(cand.roleTitle || cand.company || cand.experienceYears > 0 || cand.location) && (
                              <span> • </span>
                            )}
                            <span style={{ color: "var(--ink)", fontWeight: 500 }}>{cand.email}</span>
                          </>
                        )}
                      </div>

                      {/* Skills Chips Row */}
                      {cand.skills.length > 0 && (
                        <div className="candidate-skills-list">
                          {cand.skills.slice(0, 5).map((skill) => (
                            <span key={skill} className="candidate-skill-tag">
                              {skill}
                            </span>
                          ))}
                          {cand.skills.length > 5 && (
                            <span className="candidate-skill-tag" style={{ color: "var(--ink-mute)" }}>
                              +{cand.skills.length - 5} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right column — the score(s) that actually matter, kept apart so a resume
                      match and an interview result (two different measurements) are never read
                      as the same number. Only renders a ring when a real score exists. */}
                  {(cand.systemStatus === "ai_matched" && cand.matchScore != null) || cand.interviewScore != null ? (
                    <div
                      className="candidate-card-right"
                      style={{ display: "flex", alignItems: "flex-start", gap: 14, flexShrink: 0 }}
                    >
                      {cand.systemStatus === "ai_matched" && cand.matchScore != null && (
                        <MatchRing score={cand.matchScore} label="AI match" color={ringColor(cand.matchScore)} />
                      )}
                      {cand.interviewScore != null && (
                        <MatchRing score={cand.interviewScore} label={cand.interviewBucket ? rankBucketLabel(cand.interviewBucket, cand.rankingBasis ?? "interview") : "Interview score"} color={ringColor(cand.interviewScore)} />
                      )}
                    </div>
                  ) : null}
                </div>

                {/* Last Line: Attached Resume Document on Left + Action Controls on Right */}
                <div className="candidate-bottom-row" style={{ marginTop: 6, paddingTop: 6, borderTop: "1px dashed var(--line)" }}>
                  <div>
                    {cand.hasResume && cand.resumeFileName ? (
                      <a
                        href="#preview"
                        className="candidate-doc-link"
                        onClick={(e) => {
                          e.preventDefault();
                          setPreviewCandidate(cand);
                        }}
                      >
                        <FileText style={{ width: 13, height: 13, color: "var(--teal)" }} />
                        <span>{cand.resumeFileName}</span>
                      </a>
                    ) : (
                      <span
                        className="candidate-doc-link"
                        style={{ color: "var(--ink-mute)", background: "var(--surface-2)", padding: "3px 10px", borderRadius: 999, border: "1px solid var(--line)" }}
                        onClick={(e) => {
                          e.preventDefault();
                          setPreviewCandidate(cand);
                        }}
                      >
                        <Mail style={{ width: 12, height: 12 }} />
                        <span>{cand.missingResumeEmailSent ? "CV requested" : "No CV yet"}</span>
                      </span>
                    )}
                  </div>

                  {/* Right Actions Group brought to last line */}
                  <div
                    className="candidate-actions-group"
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {renderStageControl(cand)}

                    {/* Action Buttons: Flexible Workflow (Run AI Match / Skip Matching / Move to Interview / Request CV / Interview Report) */}
                    {cand.systemStatus === "pending_resume" ? (
                      <button
                        type="button"
                        className="btn-start-interview-outline"
                        style={{
                          padding: "4.5px 10px",
                          fontSize: 12,
                          fontWeight: 600,
                          borderRadius: 6,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                        onClick={() => handleSendMissingResumeLink(cand)}
                        title="Send candidate a link to upload their CV"
                      >
                        <Mail style={{ width: 12, height: 12 }} />
                        <span>{cand.missingResumeEmailSent ? "CV requested" : "Request CV"}</span>
                      </button>
                    ) : cand.systemStatus === "extracted" ? (
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <button
                          type="button"
                          className="btn-start-interview-outline"
                          style={{
                            padding: "4.5px 9px",
                            fontSize: 12,
                            fontWeight: 600,
                            borderRadius: 6,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            borderColor: "color-mix(in srgb, var(--teal) 35%, var(--line))",
                            color: "var(--teal)",
                            background: "var(--teal-wash)",
                          }}
                          onClick={(e) => handleRunAIMatch(cand, e)}
                          title="Evaluate resume against job description and calculate match score"
                        >
                          <span>Run AI match</span>
                        </button>
                        <button
                          type="button"
                          className="btn-start-interview-outline"
                          style={{
                            padding: "4.5px 9px",
                            fontSize: 12,
                            fontWeight: 600,
                            borderRadius: 6,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                          onClick={(e) => handleSkipAIMatch(cand.id, "screening", e)}
                          title="Skip AI matching and fast-track candidate directly"
                        >
                          <span>Skip Matching</span>
                        </button>
                        <button
                          type="button"
                          className="btn-start-interview-outline"
                          style={{
                            padding: "4.5px 9px",
                            fontSize: 12,
                            fontWeight: 600,
                            borderRadius: 6,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                          onClick={(e) => handleMoveToInterview(cand.id, e)}
                          title="Bypass matching/screening and dispatch voice AI interview link immediately"
                        >
                          <Send style={{ width: 11, height: 11 }} />
                          <span>Move to Interview</span>
                        </button>
                      </div>
                    ) : cand.systemStatus === "match_skipped" ? (
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <button
                          type="button"
                          className="btn-start-interview-outline"
                          style={{
                            padding: "4.5px 9px",
                            fontSize: 12,
                            fontWeight: 600,
                            borderRadius: 6,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            borderColor: "color-mix(in srgb, var(--teal) 35%, var(--line))",
                            color: "var(--teal)",
                            background: "var(--teal-wash)",
                          }}
                          onClick={(e) => handleRunAIMatch(cand, e)}
                          title="Run on-demand AI matching score for this candidate"
                        >
                          <span>Run AI match</span>
                        </button>
                        <button
                          type="button"
                          className="btn-start-interview-outline"
                          style={{
                            padding: "4.5px 9px",
                            fontSize: 12,
                            fontWeight: 600,
                            borderRadius: 6,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                          onClick={(e) => handleMoveToInterview(cand.id, e)}
                          title="Dispatch voice AI interview link"
                        >
                          <Send style={{ width: 11, height: 11 }} />
                          <span>Send Interview</span>
                        </button>
                      </div>
                    ) : cand.isInterviewed ? (
                      <Link
                        to={cand.candidacyId ? `/app/review/${cand.candidacyId}` : `/app/interviews`}
                        className="btn-interview-cta"
                        style={{
                          padding: "4.5px 11px",
                          fontSize: 12,
                          fontWeight: 600,
                          borderRadius: 6,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          background: "var(--violet-wash)",
                          border: "1px solid color-mix(in srgb, var(--violet) 35%, var(--line))",
                          color: "var(--violet)",
                          textDecoration: "none",
                        }}
                        title="View AI interview transcript and evaluation scores"
                      >
                        <FileText style={{ width: 12, height: 12, color: "var(--violet)" }} />
                        <span>View Interview</span>
                      </Link>
                    ) : (
                      <button
                        type="button"
                        className="btn-start-interview-outline"
                        style={{
                          padding: "4.5px 10px",
                          fontSize: 12,
                          fontWeight: 600,
                          borderRadius: 6,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                        onClick={(e) => handleMoveToInterview(cand.id, e)}
                        title="Dispatch personalized voice AI interview link"
                      >
                        <Send style={{ width: 12, height: 12 }} />
                        <span>Send Interview</span>
                      </button>
                    )}

                    {/* Approve Check Button */}
                    <button
                      type="button"
                      className={`btn-advance-candidate ${isApproved ? "approved" : ""}`}
                      style={{ width: 28, height: 28, borderRadius: 6 }}
                      onClick={() => handleApproveCandidate(cand)}
                      data-tip={isApproved ? "Clear approval" : "Approve"}
                      data-tip-pos="top"
                      aria-label={isApproved ? `Clear approval for ${cand.name}` : `Approve ${cand.name}`}
                    >
                      <Check style={{ width: 14, height: 14 }} />
                    </button>

                    {/* Reject / Archive Button */}
                    <button
                      type="button"
                      className="btn-reject-candidate"
                      style={{ width: 28, height: 28, borderRadius: 6 }}
                      onClick={() => requestRejectCandidate(cand)}
                      data-tip="Reject…"
                      data-tip-pos="left"
                      aria-label={`Reject ${cand.name}`}
                    >
                      <X style={{ width: 13, height: 13 }} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Pagination Controls Bar (30 resumes per page) */}
          {totalCandidates > 0 && (
            <div className="candidates-pagination-bar">
              <div className="candidates-pagination-info">
                Showing <strong>{((currentPage - 1) * PAGE_SIZE) + 1}</strong>–<strong>{Math.min(currentPage * PAGE_SIZE, totalCandidates)}</strong> of <strong>{totalCandidates}</strong> candidate{totalCandidates === 1 ? "" : "s"}
              </div>

              {totalPages > 1 && (
                <div className="candidates-pagination-controls">
                  <button
                    type="button"
                    className="btn-pagination-nav"
                    disabled={currentPage === 1}
                    onClick={() => {
                      setCurrentPage((p) => Math.max(1, p - 1));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    title="Previous Page"
                  >
                    ← Previous
                  </button>

                  {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((page) => (
                    <button
                      key={page}
                      type="button"
                      className={`btn-pagination-page ${currentPage === page ? "active" : ""}`}
                      onClick={() => {
                        setCurrentPage(page);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    type="button"
                    className="btn-pagination-nav"
                    disabled={currentPage === totalPages}
                    onClick={() => {
                      setCurrentPage((p) => Math.min(totalPages, p + 1));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    title="Next Page"
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Still loading — don't assert "no candidates" over a request in flight. */}
          {candidatesLoading && allCandidates.length === 0 && (
            <div className="t1c-empty">
              <p style={{ color: "var(--ink-mute)", fontSize: 14, margin: 0 }}>Loading candidates…</p>
            </div>
          )}

          {/* TRUE EMPTY vs FILTERED EMPTY. "Reset filters" is useless advice to an org that has
              no candidates at all — what they need is a way to add one. */}
          {!candidatesLoading && allCandidates.length === 0 && (
            <div className="t1c-empty">
              <Users style={{ width: 28, height: 28, color: "var(--ink-mute)" }} />
              <h3 style={{ margin: "12px 0 6px", fontSize: 17, color: "var(--ink)" }}>No candidates yet</h3>
              <p style={{ color: "var(--ink-mute)", fontSize: 14, margin: "0 auto 18px", maxWidth: 460, lineHeight: 1.6 }}>
                {requisitionsList.length === 0
                  ? "Create a job requirement first — every candidate belongs to a role."
                  : "Upload resumes, add someone directly, or switch on Email receiving and have applications forwarded to the job's own address."}
              </p>
              <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                {requisitionsList.length === 0 ? (
                  <Link to="/app/new" className="btn primary">
                    <span>Create a requirement</span>
                  </Link>
                ) : (
                  <>
                    <button type="button" className="btn primary" onClick={openAddModal}>
                      <Plus className="btn-icon" />
                      <span>Add candidates</span>
                    </button>
                    <Link to="/app/connections" className="btn ghost">
                      <span>Set up email receiving</span>
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}

          {!candidatesLoading && allCandidates.length > 0 && filteredCandidates.length === 0 && (
            <div className="t1c-empty">
              <p style={{ color: "var(--ink-mute)", fontSize: 14, margin: "0 0 16px" }}>
                No candidates in this stage match your current filters.
              </p>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  clearAllFilters();
                  setPipelineTab("all");
                }}
              >
                <span>Reset all filters</span>
              </button>
            </div>
          )}
          {/* Sticky bulk-action bar — appears once rows are selected (audit 16). */}
          {selectedIds.size > 0 && (
            <div className="t1c-bulk" role="region" aria-label="Bulk actions">
              <div className="batch-floating-inner">
                <div className="batch-bar-left">
                  <div className="batch-counter-pill">
                    <Check style={{ width: 16, height: 16 }} />
                    <span><b>{selectedIds.size}</b> selected</span>
                  </div>
                  <span className="batch-info-sub">
                    {selectedNotInterviewedCount > 0
                      ? `(${selectedNotInterviewedCount} not yet interviewed)`
                      : "(All selected have completed interview)"}
                  </span>
                  <button
                    type="button"
                    className="batch-clear-btn"
                    onClick={clearSelection}
                  >
                    Clear
                  </button>
                </div>

                <div className="batch-bar-right">
                  <button
                    type="button"
                    className="batch-primary-dispatch-btn"
                    onClick={() => {
                      setBatchStep("confirm");
                      setBatchModalOpen(true);
                    }}
                  >
                    <Send style={{ width: 16, height: 16 }} />
                    <span>Send interview links ({selectedIds.size})</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Already asked — confirm before a second link replaces the first. */}
      {confirmResend && (
        <Modal
          title={`Request ${confirmResend.name}'s CV again?`}
          onClose={() => setConfirmResend(null)}
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button type="button" className="btn ghost" onClick={() => setConfirmResend(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  requestInfo.mutate(confirmResend);
                  setConfirmResend(null);
                }}
              >
                Request again
              </button>
            </div>
          }
        >
          <div className="mut sm" style={{ lineHeight: 1.6 }}>
            {cvRequestSummary(confirmResend.cvRequest) ?? "A CV request was already sent"}. Requesting again
            emails {confirmResend.email || "them"} a new link and the earlier link stops working. You'll get the
            new link to copy, so you can also share it on WhatsApp.
          </div>
        </Modal>
      )}

      {/* Reject — a human decision, confirmed (GROUND_RULES: rejection is always human-gated). */}
      {confirmReject && (
        <Modal
          title={`Reject ${confirmReject.name}?`}
          onClose={() => setConfirmReject(null)}
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button type="button" className="btn ghost" onClick={() => setConfirmReject(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn danger"
                onClick={() => {
                  handleRejectCandidate(confirmReject);
                  setConfirmReject(null);
                }}
              >
                Reject
              </button>
            </div>
          }
        >
          <div className="mut sm" style={{ lineHeight: 1.6 }}>
            This marks {confirmReject.name} as rejected for {confirmReject.reqTitle || "this role"} in your view.
            It's your decision — the AI only ranks — and you can undo it from the row's menu.
          </div>
        </Modal>
      )}

      {/* Batch Interview Link Dispatch Modal */}
      {batchModalOpen && (
        <div className="modal-backdrop" onClick={() => setBatchModalOpen(false)}>
          <div
            className="questions-modal-card"
            style={{ maxWidth: 680 }}
            onClick={(e) => e.stopPropagation()}
          >
            {batchStep === "confirm" ? (
              <>
                <div className="questions-modal-header">
                  <div>
                    <h3 className="questions-modal-title" style={{ fontSize: 18 }}>
                      Send AI Interview Links
                    </h3>
                    <p className="questions-modal-desc" style={{ margin: "2px 0 0" }}>
                      Email the voice AI interview link to {selectedCandidateList.length} candidate{selectedCandidateList.length > 1 ? "s" : ""}. Uses one
                      AI interview from your plan each; anyone already invited is skipped and not charged.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="questions-modal-close"
                    aria-label="Close"
                    data-tip="Close"
                    data-tip-pos="left"
                    onClick={() => setBatchModalOpen(false)}
                  >
                    <X style={{ width: 16, height: 16 }} />
                  </button>
                </div>

                <div style={{ padding: "20px 24px", maxHeight: "60vh", overflowY: "auto" }}>
                  {/* Summary Metric Strip */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
                    <div style={{ background: "var(--teal-wash)", border: "1px solid var(--line)", padding: 12, borderRadius: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--teal)", textTransform: "uppercase" }}>
                        Selected Candidates
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: "var(--teal)", marginTop: 4 }}>
                        {selectedCandidateList.length}
                      </div>
                    </div>

                    <div style={{ background: "var(--surface-2)", border: "1px solid var(--line)", padding: 12, borderRadius: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-mute)", textTransform: "uppercase" }}>
                        Interview Format
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", marginTop: 6 }}>
                        Voice AI (15-20 Min)
                      </div>
                    </div>

                    <div style={{ background: "var(--surface-2)", border: "1px solid var(--line)", padding: 12, borderRadius: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-mute)", textTransform: "uppercase" }}>
                        Link Expiry
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", marginTop: 6 }}>
                        7 Calendar Days
                      </div>
                    </div>
                  </div>

                  {/* Recipient Candidates List */}
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <label className="field-label-bold" style={{ margin: 0 }}>
                        RECIPIENTS ({selectedCandidateList.length})
                      </label>
                      <span style={{ fontSize: 12, color: "var(--ink-mute)" }}>
                        Click ✕ on any candidate to exclude from this batch
                      </span>
                    </div>

                    <div className="batch-recipient-list">
                      {selectedCandidateList.map((c) => (
                        <div key={c.id} className="batch-recipient-item">
                          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                            <div className={`candidate-avatar ${c.avatarColor}`} style={{ width: 30, height: 30, fontSize: 12 }}>
                              {c.initials}
                            </div>
                            <div style={{ minWidth: 0, overflow: "hidden" }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
                                {c.name}
                              </div>
                              <div style={{ fontSize: 12, color: "var(--ink-mute)", whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
                                {c.email} • {c.roleTitle}
                              </div>
                            </div>
                          </div>

                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                            {c.isInterviewed ? (
                              <span className="pill-interviewed" style={{ fontSize: 12, padding: "2px 6px" }}>
                                Already Interviewed
                              </span>
                            ) : (
                              <span className="pill-not-interviewed" style={{ fontSize: 12, padding: "2px 6px" }}>
                                Ready to Invite
                              </span>
                            )}
                            <button
                              type="button"
                              className="btn-remove-recipient"
                              onClick={() => removeCandidateFromBatch(c.id)}
                              aria-label={`Remove ${c.name} from this batch`}
                              data-tip="Remove from batch"
                              data-tip-pos="left"
                            >
                              <X style={{ width: 13, height: 13 }} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Dispatch Notice Box */}
                  <div style={{ background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: 8, padding: 14 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ink)", marginBottom: 4 }}>
                      What happens when you send?
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "var(--ink-mute)", lineHeight: 1.6 }}>
                      <li>Each candidate receives an email invitation containing a unique, tamper-proof capability token.</li>
                      <li>You will also get a full list of shareable interview links right away to copy into WhatsApp, Slack, or your ATS.</li>
                      <li>Candidate evaluations automatically sync into your <b>Interviews</b> hub once completed.</li>
                    </ul>
                  </div>
                </div>

                {dispatchError && (
                  <p role="alert" style={{ margin: "0 24px 12px", color: "var(--crit)", fontSize: 13 }}>{dispatchError}</p>
                )}
                <div className="questions-modal-footer">
                  <button
                    type="button"
                    className="btn-modal-cancel"
                    onClick={() => setBatchModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-save-questions-green"
                    disabled={isDispatching || selectedCandidateList.length === 0}
                    onClick={handleBatchDispatch}
                  >
                    <Send style={{ width: 14, height: 14 }} />
                    <span>
                      {isDispatching
                        ? "Dispatching..."
                        : `Dispatch ${selectedCandidateList.length} Interview Links Now →`}
                    </span>
                  </button>
                </div>
              </>
            ) : (
              /* Success & Generated Links Step */
              <>
                <div className="questions-modal-header" style={{ borderBottomColor: "color-mix(in srgb, var(--teal) 35%, var(--line))", background: "var(--teal-wash)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--teal)", color: "var(--surface)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Check style={{ width: 20, height: 20 }} />
                    </div>
                    <div>
                      <h3 className="questions-modal-title" style={{ fontSize: 17, color: "var(--teal)" }}>
                        Interview Links Dispatched Successfully!
                      </h3>
                      <p className="questions-modal-desc" style={{ color: "var(--teal)", margin: 0 }}>
                        {generatedBatchLinks.length} candidate{generatedBatchLinks.length === 1 ? " was" : "s were"} emailed their voice AI interview link.
                        {dispatchSkipped > 0 && ` ${dispatchSkipped} already invited — skipped, not charged.`}
                        {dispatchError && ` ${dispatchError}`}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="questions-modal-close"
                    aria-label="Close"
                    data-tip="Close"
                    data-tip-pos="left"
                    onClick={() => {
                      setBatchModalOpen(false);
                      clearSelection();
                    }}
                  >
                    <X style={{ width: 16, height: 16 }} />
                  </button>
                </div>

                <div style={{ padding: "20px 24px", maxHeight: "60vh", overflowY: "auto" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>
                        Generated Interview Capability Links
                      </div>
                      <div style={{ fontSize: 12, color: "var(--ink-mute)" }}>
                        Share directly via WhatsApp, email, or ATS tracking notes.
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-copy-all-links"
                      onClick={handleCopyAllLinks}
                    >
                      {copiedBatchAll ? (
                        <>
                          <Check style={{ width: 14, height: 14 }} />
                          <span>Copied All {generatedBatchLinks.length} Links!</span>
                        </>
                      ) : (
                        <>
                          <Copy style={{ width: 14, height: 14 }} />
                          <span>Copy All Links</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Individual Links Table */}
                  <div className="batch-links-table-container">
                    {generatedBatchLinks.map((item) => (
                      <div key={item.id} className="batch-link-row">
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>
                            {item.name}
                          </div>
                          <div style={{ fontSize: 12, color: "var(--ink-mute)", marginBottom: 4 }}>
                            {item.email}
                          </div>
                          <div className="batch-link-url-code">
                            {item.link}
                          </div>
                        </div>

                        <button
                          type="button"
                          className="btn-copy-single-link"
                          onClick={() => handleCopySingleLink(item.link, item.id)}
                          title="Copy link to clipboard"
                        >
                          {copiedSingleId === item.id ? (
                            <>
                              <Check style={{ width: 13, height: 13, color: "var(--teal)" }} />
                              <span style={{ color: "var(--teal)" }}>Copied</span>
                            </>
                          ) : (
                            <>
                              <Copy style={{ width: 13, height: 13 }} />
                              <span>Copy</span>
                            </>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="questions-modal-footer">
                  <button
                    type="button"
                    className="btn-save-questions-green"
                    onClick={() => {
                      setBatchModalOpen(false);
                      clearSelection();
                    }}
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Candidate Resume & Credentials Preview Modal */}
      {previewCandidate && (
        <div className="modal-backdrop" onClick={() => setPreviewCandidate(null)}>
          <div
            className="questions-modal-card"
            style={{ maxWidth: 640 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="questions-modal-header">
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div className={`candidate-avatar ${previewCandidate.avatarColor}`}>
                  {previewCandidate.initials}
                </div>
                <div>
                  <h3 className="questions-modal-title" style={{ fontSize: 17 }}>
                    {previewCandidate.name}
                  </h3>
                  <p className="questions-modal-desc" style={{ margin: 0 }}>
                    {previewCandidate.roleTitle} • {previewCandidate.company}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="questions-modal-close"
                    aria-label="Close"
                    data-tip="Close"
                    data-tip-pos="left"
                onClick={() => setPreviewCandidate(null)}
              >
                <X style={{ width: 16, height: 16 }} />
              </button>
            </div>

            <div style={{ padding: "20px 24px", maxHeight: "65vh", overflowY: "auto" }}>
              {/* Top Highlights Grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
                <div style={{ background: "var(--surface-2)", padding: 12, borderRadius: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-mute)", textTransform: "uppercase" }}>
                    AI Match Score
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: previewCandidate.matchScore ? "var(--teal)" : "var(--warn)", marginTop: 4 }}>
                    {previewCandidate.matchScore ? `${previewCandidate.matchScore}%` : "Skipped (Direct HR)"}
                  </div>
                </div>

                <div style={{ background: "var(--surface-2)", padding: 12, borderRadius: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-mute)", textTransform: "uppercase" }}>
                    Experience
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "var(--ink)", marginTop: 4 }}>
                    {previewCandidate.experienceYears} Years
                  </div>
                </div>

                <div style={{ background: "var(--surface-2)", padding: 12, borderRadius: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-mute)", textTransform: "uppercase" }}>
                    Interview Status
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: previewCandidate.isInterviewed ? "var(--violet)" : "var(--warn)", marginTop: 4 }}>
                    {previewCandidate.isInterviewed
                      ? previewCandidate.interviewScore != null
                        ? `${previewCandidate.interviewScore} · ${previewCandidate.interviewBucket ? rankBucketLabel(previewCandidate.interviewBucket, previewCandidate.rankingBasis ?? "interview") : "Interview score"}`
                        : "Interviewed"
                      : "Not Interviewed"}
                  </div>
                </div>
              </div>

              {/* Requirement & Source */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-mute)", textTransform: "uppercase", marginBottom: 6 }}>
                  Target Requirement &amp; Source
                </div>
                <div style={{ fontSize: 13, color: "var(--ink)", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <span className="pill-target-req">{previewCandidate.reqTitle}</span>
                  <span className="pill-channel-email">{previewCandidate.channel}</span>
                  <span className="pill-stage-badge">Stage: {previewCandidate.pipelineStage}</span>
                  {previewCandidate.systemStatus === "match_skipped" && (
                    <span className="pill-match-skipped">Match Skipped</span>
                  )}
                  {previewCandidate.systemStatus === "pending_resume" && (
                    <span className="pill-pending-resume">Pending Resume CV</span>
                  )}
                </div>
              </div>

              {/* Contact Details */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-mute)", textTransform: "uppercase", marginBottom: 6 }}>
                  Contact Information
                </div>
                <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>
                  Email: <b>{previewCandidate.email}</b> • Location: <b>{previewCandidate.location}</b>
                </div>
              </div>

              {/* Everything that happened to this person on this requirement — emails, link opened,
                  form submitted, resume, interview, decisions — dated (GET /candidacies/{id}/timeline). */}
              {previewCandidate.candidacyId && (
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-mute)", textTransform: "uppercase", marginBottom: 8 }}>
                    Activity log
                  </div>
                  <CandidateTimeline candidacyId={previewCandidate.candidacyId} />
                </div>
              )}

              {/* Verified Technical Skills */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-mute)", textTransform: "uppercase", marginBottom: 8 }}>
                  Candidate Skills &amp; Extracted Profile
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {previewCandidate.skills.map((s) => (
                    <span key={s} className="req-skill-chip" style={{ background: "var(--teal-wash)", color: "var(--teal)", borderColor: "color-mix(in srgb, var(--teal) 35%, var(--line))" }}>
                      ✓ {s}
                    </span>
                  ))}
                </div>
              </div>

              {/* Attached Resume Document or Pending Upload Notice */}
              {previewCandidate.hasResume && previewCandidate.resumeFileName ? (
                <div style={{ background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: 8, padding: 14, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <FileText style={{ width: 18, height: 18, color: "var(--teal)" }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>
                        {previewCandidate.resumeFileName}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--ink-mute)" }}>
                        Candidate resume document (PDF)
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-modal-cancel"
                    style={{ fontSize: 12, padding: "6px 12px" }}
                    onClick={() => alert(`Downloading resume: ${previewCandidate.resumeFileName}`)}
                  >
                    Download PDF
                  </button>
                </div>
              ) : (
                <div style={{ background: "var(--crit-wash)", border: "1px solid color-mix(in srgb, var(--crit) 35%, var(--line))", borderRadius: 8, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                  <Mail style={{ width: 18, height: 18, color: "var(--crit)", flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--crit)" }}>
                      {previewCandidate.channel === "In-System Email"
                        ? "No Resume Attached (Candidate Ingested via Email)"
                        : "No Resume on File Yet"}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--crit)", marginTop: 2 }}>
                      {requestInfo.isPending && requestInfo.variables?.id === previewCandidate.id ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600, color: "var(--crit)" }}>
                          <Loader2 className="spin" style={{ width: 13, height: 13 }} />
                          <span>Sending secure CV upload link…</span>
                        </span>
                      ) : (previewCandidate.missingResumeEmailSent || missingResumeInvites[previewCandidate.id]) ? (
                        <>
                          {cvRequestSummary(previewCandidate.cvRequest) ?? "A secure CV upload link has been sent to the candidate"}.{" "}
                          <button
                            type="button"
                            onClick={() => handleSendMissingResumeLink(previewCandidate)}
                            disabled={requestInfo.isPending}
                            style={{
                              background: "none",
                              border: "none",
                              padding: 0,
                              color: "var(--crit)",
                              fontWeight: 600,
                              textDecoration: "underline",
                              cursor: "pointer",
                              fontSize: "inherit",
                              display: "inline",
                            }}
                          >
                            Request again
                          </button>
                          {lastCvLink?.id === previewCandidate.id && (
                            <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 8 }}>
                              <code
                                title={lastCvLink.link}
                                style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11.5, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 6, padding: "4px 8px", color: "var(--ink)" }}
                              >
                                {lastCvLink.link}
                              </code>
                              <button type="button" className="btn sm" onClick={() => copyCvLink(lastCvLink.link)}>
                                {linkCopied ? "Copied" : "Copy link"}
                              </button>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          Candidate applied without attaching a resume file.{" "}
                          <button
                            type="button"
                            onClick={() => handleSendMissingResumeLink(previewCandidate)}
                            disabled={requestInfo.isPending}
                            style={{
                              background: "none",
                              border: "none",
                              padding: 0,
                              color: "var(--crit)",
                              fontWeight: 600,
                              textDecoration: "underline",
                              cursor: "pointer",
                              fontSize: "inherit",
                              display: "inline",
                            }}
                          >
                            Send link now
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="questions-modal-footer">
              <div className="questions-footer-hint">
                Candidate authenticated and compliant with DPDP guidelines.
              </div>
              <div className="questions-footer-actions">
                <button
                  type="button"
                  className="btn-modal-cancel"
                  onClick={() => setPreviewCandidate(null)}
                >
                  Close
                </button>

                {/* On-demand Run AI Match if not matched */}
                {previewCandidate.hasResume && (previewCandidate.systemStatus === "extracted" || previewCandidate.systemStatus === "match_skipped") && (
                  <button
                    type="button"
                    className="btn-start-interview-outline"
                    style={{
                      padding: "6px 12px",
                      fontSize: 12.5,
                      fontWeight: 600,
                      borderRadius: 8,
                      borderColor: "color-mix(in srgb, var(--teal) 35%, var(--line))",
                      color: "var(--teal)",
                      background: "var(--teal-wash)",
                    }}
                    onClick={() => {
                      // No optimistic score. The previous version wrote 92 into the row before the
                      // request was made, so the recruiter read a score the scorer had not produced
                      // — and kept reading it if the call then degraded. The list refreshes from
                      // the server on success instead.
                      handleRunAIMatch(previewCandidate);
                      setPreviewCandidate(null);
                    }}
                  >
                    <span>Run AI match</span>
                  </button>
                )}

                {previewCandidate.isInterviewed ? (
                  <Link
                    to={previewCandidate.candidacyId ? `/app/review/${previewCandidate.candidacyId}` : `/app/interviews`}
                    className="btn-save-questions-green"
                    style={{ textDecoration: "none" }}
                  >
                    <span>View Interview →</span>
                  </Link>
                ) : (previewCandidate.systemStatus === "pending_resume" || !previewCandidate.hasResume) ? (
                  <button
                    type="button"
                    className="btn-save-questions-green"
                    disabled={requestInfo.isPending}
                    onClick={() => {
                      handleSendMissingResumeLink(previewCandidate);
                    }}
                  >
                    {requestInfo.isPending && requestInfo.variables?.id === previewCandidate.id ? (
                      <>
                        <Loader2 className="spin" style={{ width: 14, height: 14 }} />
                        <span>Sending…</span>
                      </>
                    ) : (
                      <>
                        <Mail style={{ width: 14, height: 14 }} />
                        <span>
                          {(previewCandidate.missingResumeEmailSent || missingResumeInvites[previewCandidate.id])
                            ? "Request CV Again"
                            : "Request CV via Email"}
                        </span>
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn-save-questions-green"
                    onClick={() => {
                      setSelectedIds(new Set([previewCandidate.id]));
                      setPreviewCandidate(null);
                      setBatchStep("confirm");
                      setBatchModalOpen(true);
                    }}
                  >
                    <Send style={{ width: 14, height: 14 }} />
                    <span>Send Interview Invite</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 3-Mode Add Candidates Modal */}
      {uploadModalOpen && (
        <div className="modal-backdrop" onClick={() => setUploadModalOpen(false)}>
          <div className="questions-modal-card" style={{ maxWidth: 580 }} onClick={(e) => e.stopPropagation()}>
            <div className="questions-modal-header">
              <div>
                <h3 className="questions-modal-title">Add Candidate</h3>
                <p className="questions-modal-desc" style={{ margin: "2px 0 0" }}>
                  Add candidates via bulk upload, direct fast-track, or request a resume.
                </p>
              </div>
              <button
                type="button"
                className="questions-modal-close"
                    aria-label="Close"
                    data-tip="Close"
                    data-tip-pos="left"
                onClick={() => setUploadModalOpen(false)}
              >
                <X style={{ width: 16, height: 16 }} />
              </button>
            </div>

            {/* Server feedback INSIDE the dialog. A failure note rendered on the page behind an
                open modal is a failure the user never sees — they press the button again and
                conclude the app is broken. The allowance 402 is the common case here. */}
            {actionNote && (
              <div style={{ padding: "12px 16px 0" }}>
                <div className="banner warn" style={{ margin: 0 }}>
                  <Alert />
                  <div>{actionNote}</div>
                </div>
              </div>
            )}

            {/* 3 Mode Navigation Tabs */}
            <div style={{ display: "flex", borderBottom: "1px solid var(--line)", background: "var(--surface-2)", padding: "0 16px" }}>
              <button
                type="button"
                style={{
                  flex: 1,
                  padding: "12px 8px",
                  fontSize: 12.5,
                  fontWeight: 700,
                  border: "none",
                  background: "none",
                  borderBottom: addCandidateMode === "bulk_match" ? "2px solid var(--teal)" : "2px solid transparent",
                  color: addCandidateMode === "bulk_match" ? "var(--teal)" : "var(--ink-mute)",
                  cursor: "pointer",
                }}
                onClick={() => setAddCandidateMode("bulk_match")}
              >
                Upload CVs
              </button>
              <button
                type="button"
                style={{
                  flex: 1,
                  padding: "12px 8px",
                  fontSize: 12.5,
                  fontWeight: 700,
                  border: "none",
                  background: "none",
                  borderBottom: addCandidateMode === "direct_fasttrack" ? "2px solid var(--teal)" : "2px solid transparent",
                  color: addCandidateMode === "direct_fasttrack" ? "var(--teal)" : "var(--ink-mute)",
                  cursor: "pointer",
                }}
                onClick={() => setAddCandidateMode("direct_fasttrack")}
              >
                ⏩ Direct Add
              </button>
              <button
                type="button"
                style={{
                  flex: 1,
                  padding: "12px 8px",
                  fontSize: 12.5,
                  fontWeight: 700,
                  border: "none",
                  background: "none",
                  borderBottom: addCandidateMode === "request_email" ? "2px solid var(--teal)" : "2px solid transparent",
                  color: addCandidateMode === "request_email" ? "var(--teal)" : "var(--ink-mute)",
                  cursor: "pointer",
                }}
                onClick={() => setAddCandidateMode("request_email")}
              >
                ✉️ Request CV
              </button>
            </div>

            <div style={{ padding: "20px 24px", maxHeight: "60vh", overflowY: "auto" }}>
              {/* Target Requirement Assignment (Common for all modes) */}
              <div style={{ marginBottom: 18 }}>
                <label className="field-label-bold" style={{ display: "block", marginBottom: 6 }}>
                  ASSIGN TO TARGET REQUIREMENT
                </label>
                <select
                  className="resumes-filter-select"
                  value={uploadTargetReqId || activeReqId}
                  onChange={(e) => setUploadTargetReqId(e.target.value)}
                >
                  {reqOptions.map((req) => (
                    <option key={req.id} value={req.id}>
                      {req.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Mode 1: Bulk Upload & AI Match */}
              {addCandidateMode === "bulk_match" && (
                <div>
                  <div
                    className={`t1-cvdrop${dragging ? " is-drag" : ""}`}
                    role="button"
                    tabIndex={0}
                    aria-label="Choose or drop PDF / Word resumes"
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), fileInputRef.current?.click())}
                    // Drag & drop: preventDefault on dragover is what lets the browser deliver a drop here
                    // at all (without it the browser opens the PDF instead) — it was missing entirely.
                    onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; if (!dragging) setDragging(true); }}
                    onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false); }}
                    onDrop={(e) => { e.preventDefault(); setDragging(false); void handleCvFiles(e.dataTransfer.files); }}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      accept=".pdf,.doc,.docx"
                      multiple
                      onChange={handleFileUpload}
                    />
                    <UploadCloud style={{ width: 36, height: 36, color: "var(--teal)", margin: "0 auto 12px" }} />
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)" }}>
                      {dragging ? "Drop to upload" : "Drop CVs here, or click to choose PDF / Word resumes"}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--ink-mute)", marginTop: 4 }}>
                      Files are extracted, evaluated against requirement skills, and scored by AI. Up to 10 MB each.
                    </div>
                  </div>
                  <CvUploadStatus items={uploads} />

                  <div style={{ background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: 8, padding: 12, fontSize: 12, color: "var(--ink-mute)" }}>
                    💡 <b>Automatic AI Matching:</b> Evaluates candidate skills against the target requirement and adds ranked results to the <b>Matches</b> tab.
                  </div>
                </div>
              )}

              {/* Mode 2: Direct Add & Fast-Track (Skip AI Match) */}
              {addCandidateMode === "direct_fasttrack" && (
                <form onSubmit={handleDirectAddSubmit}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <div>
                      <label className="field-label-bold" style={{ display: "block", marginBottom: 4 }}>
                        CANDIDATE FULL NAME *
                      </label>
                      <input
                        type="text"
                        className="field-input"
                        placeholder="e.g. Rahul Sharma"
                        value={directName}
                        onChange={(e) => setDirectName(e.target.value)}
                        required
                        style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid var(--line)", fontSize: 13 }}
                      />
                    </div>
                    <div>
                      <label className="field-label-bold" style={{ display: "block", marginBottom: 4 }}>
                        EMAIL ADDRESS *
                      </label>
                      <input
                        type="email"
                        className="field-input"
                        placeholder="e.g. rahul@example.com"
                        value={directEmail}
                        onChange={(e) => setDirectEmail(e.target.value)}
                        required
                        style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid var(--line)", fontSize: 13 }}
                      />
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                    <div>
                      <label className="field-label-bold" style={{ display: "block", marginBottom: 4 }}>
                        CURRENT ROLE / HEADLINE
                      </label>
                      <input
                        type="text"
                        className="field-input"
                        placeholder="e.g. Senior Backend Engineer"
                        value={directRole}
                        onChange={(e) => setDirectRole(e.target.value)}
                        style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid var(--line)", fontSize: 13 }}
                      />
                    </div>
                    <div>
                      <label className="field-label-bold" style={{ display: "block", marginBottom: 4 }}>
                        EXPERIENCE (YEARS)
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={40}
                        className="field-input"
                        value={directExperience}
                        onChange={(e) => setDirectExperience(Number(e.target.value))}
                        style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid var(--line)", fontSize: 13 }}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label className="field-label-bold" style={{ display: "block", marginBottom: 4 }}>
                      FAST-TRACK DIRECTLY TO STAGE
                    </label>
                    <select
                      className="resumes-filter-select"
                      value={directStage}
                      onChange={(e) => setDirectStage(e.target.value as any)}
                    >
                      <option value="interview">Interview (Ready to dispatch voice AI interview link)</option>
                      <option value="screening">Screening</option>
                      <option value="shortlisted">Shortlisted</option>
                    </select>
                  </div>

                  <div style={{ background: "var(--warn-wash)", border: "1px solid color-mix(in srgb, var(--warn) 35%, var(--line))", borderRadius: 8, padding: 12, fontSize: 12, color: "var(--warn)", marginBottom: 12 }}>
                    ⏩ <b>AI Matching Skipped:</b> Ideal when HR already found a suitable candidate. The candidate will be added directly into the selected stage with full manual controls.
                  </div>

                  <div className="questions-modal-footer" style={{ padding: "12px 0 0", borderTop: "none" }}>
                    <button
                      type="button"
                      className="btn-modal-cancel"
                      onClick={() => setUploadModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-save-questions-green"
                    >
                      Add &amp; Fast-Track Candidate
                    </button>
                  </div>
                </form>
              )}

              {/* Mode 3: Request Resume via Email */}
              {addCandidateMode === "request_email" && (
                <form onSubmit={handleRequestResumeSubmit}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <div>
                      <label className="field-label-bold" style={{ display: "block", marginBottom: 4 }}>
                        CANDIDATE FULL NAME *
                      </label>
                      <input
                        type="text"
                        className="field-input"
                        placeholder="e.g. Anjali Mehta"
                        value={requestName}
                        onChange={(e) => setRequestName(e.target.value)}
                        required
                        style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid var(--line)", fontSize: 13 }}
                      />
                    </div>
                    <div>
                      <label className="field-label-bold" style={{ display: "block", marginBottom: 4 }}>
                        CANDIDATE EMAIL *
                      </label>
                      <input
                        type="email"
                        className="field-input"
                        placeholder="e.g. anjali@domain.com"
                        value={requestEmail}
                        onChange={(e) => setRequestEmail(e.target.value)}
                        required
                        style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid var(--line)", fontSize: 13 }}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <label className="field-label-bold" style={{ display: "block", marginBottom: 4 }}>
                      CUSTOM MESSAGE / INVITATION NOTE (OPTIONAL)
                    </label>
                    <textarea
                      className="field-input"
                      rows={3}
                      placeholder="Hi Anjali, thank you for reaching out. Please upload your latest CV to complete your application..."
                      value={requestCustomNote}
                      onChange={(e) => setRequestCustomNote(e.target.value)}
                      style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid var(--line)", fontSize: 12.5 }}
                    />
                  </div>

                  <div style={{ background: "var(--teal-wash)", border: "1px solid color-mix(in srgb, var(--teal) 35%, var(--line))", borderRadius: 8, padding: 12, fontSize: 12, color: "var(--teal)", marginBottom: 12 }}>
                    ✉️ <b>Resume Request Flow:</b> For applicants who emailed or inquired without attaching a CV. TalentHop creates a profile in <b>Screening (Pending Resume)</b> and emails them a secure upload link.
                  </div>

                  <div className="questions-modal-footer" style={{ padding: "12px 0 0", borderTop: "none" }}>
                    <button
                      type="button"
                      className="btn-modal-cancel"
                      onClick={() => setUploadModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-save-questions-green"
                    >
                      <Send style={{ width: 14, height: 14 }} />
                      <span>Send CV Upload Link</span>
                    </button>
                  </div>
                </form>
              )}
            </div>

            {addCandidateMode === "bulk_match" && (
              <div className="questions-modal-footer">
                <button
                  type="button"
                  className="btn-modal-cancel"
                  onClick={() => {
                    setUploadModalOpen(false);
                    // Finished rows are cleared; anything still running keeps going and stays listed.
                    setUploads((list) => list.filter((u) => u.phase === "reading" || u.phase === "uploading" || u.phase === "processing"));
                  }}
                >
                  {uploads.length > 0 && !uploadsBusy ? "Done" : "Close"}
                </button>
                <button
                  type="button"
                  className="btn-save-questions-green"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploadsBusy ? "Add more files" : "Choose Files"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Connect Intake Channel Modal */}
      <ConnectivityModal isOpen={connectModalOpen} onClose={() => setConnectModalOpen(false)} />
    </div>
  );
}
